/**
 * Flower Chat - Side Menu Chatbot UI
 * With Lambda integration and user ID management
 */

(function () {
  "use strict";

  // Wait for DOM to be ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFlowerChat);
  } else {
    initFlowerChat();
  }

  function initFlowerChat() {
    /**
     * Escape HTML entities to prevent XSS attacks
     * @param {string} text - Text to escape
     * @returns {string} Escaped text safe for HTML insertion
     */
    function escapeHtml(text) {
      if (text === null || text === undefined) return "";
      const div = document.createElement("div");
      div.textContent = String(text);
      return div.innerHTML;
    }

    /**
     * Lazy-load CSS: Inject critical button styles immediately, load full CSS on first open
     */
    let fullCssLoaded = false;

    // Critical button-only styles (injected immediately)
    // NOTE: Button hidden by default for Google A/B Testing control
    // Google Optimize will show it for test variants with: .flowerchat-button { display: flex !important; }
    const buttonStyles = document.createElement("style");
    buttonStyles.id = "flowerchat-button-styles";
    buttonStyles.textContent = `
      .flowerchat-button {
        position: fixed;
        bottom: 90px;
        right: 20px;
        height: 60px;
        min-width: 180px;
        padding: 0 20px;
        border-radius: 30px;
        background-color: #f7b7b7 !important;
        color: #333 !important;
        border: none !important;
        cursor: pointer;
        display: none;
        align-items: center;
        justify-content: center;
        gap: 10px;
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
        z-index: 10001;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transition: transform 0.2s ease;
      }
      .flowerchat-icon { flex-shrink: 0; width: 20px; height: 20px; color: inherit; }
      .flowerchat-text { flex-shrink: 0; }
      .flowerchat-button:hover { transform: scale(1.05); }
      .flowerchat-overlay { display: none; }
    `;
    document.head.appendChild(buttonStyles);

    // Function to lazy-load full CSS when chat opens
    function loadFullCss() {
      if (fullCssLoaded) return Promise.resolve();

      return new Promise((resolve) => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = window.Shopify?.routes?.root
          ? window.Shopify.routes.root + "cdn/shop/t/1/assets/flowerchat.css"
          : "/cdn/shop/t/1/assets/flowerchat.css";

        // Try to get the correct asset URL from Shopify
        const existingLink = document.querySelector(
          'link[href*="flowerchat.css"]'
        );
        if (existingLink) {
          // CSS already loaded via theme.liquid, just mark as loaded
          fullCssLoaded = true;
          resolve();
          return;
        }

        // Construct asset URL properly for Shopify
        const scriptTag = document.querySelector(
          'script[src*="flowerchat.js"]'
        );
        if (scriptTag) {
          link.href = scriptTag.src.replace("flowerchat.js", "flowerchat.css");
        }

        link.onload = () => {
          fullCssLoaded = true;
          // Remove inline button styles to avoid conflicts
          const inlineStyles = document.getElementById(
            "flowerchat-button-styles"
          );
          if (inlineStyles) inlineStyles.remove();
          resolve();
        };
        link.onerror = () => {
          console.error("Failed to load flowerchat.css");
          resolve(); // Continue anyway
        };
        document.head.appendChild(link);
      });
    }

    // Create the HTML structure
    const chatHTML = `
      <button class="flowerchat-button" aria-label="Open AI Floral Assistant chat" type="button">
        <svg class="flowerchat-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <!-- AI Brain/Neural Network Icon -->
          <circle cx="12" cy="8" r="2" fill="currentColor" opacity="0.9"/>
          <circle cx="8" cy="12" r="1.5" fill="currentColor" opacity="0.8"/>
          <circle cx="16" cy="12" r="1.5" fill="currentColor" opacity="0.8"/>
          <circle cx="6" cy="16" r="1" fill="currentColor" opacity="0.7"/>
          <circle cx="12" cy="16" r="1.5" fill="currentColor" opacity="0.8"/>
          <circle cx="18" cy="16" r="1" fill="currentColor" opacity="0.7"/>
          <!-- Connection lines -->
          <path d="M12 10L8 12M12 10L16 12M8 12L6 16M8 12L12 16M16 12L18 16M16 12L12 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.6"/>
          <!-- Sparkle accents for AI -->
          <path d="M4 6L4.5 5L5 6L4.5 7L4 6Z" fill="currentColor" opacity="0.8"/>
          <path d="M20 4L20.5 3L21 4L20.5 5L20 4Z" fill="currentColor" opacity="0.8"/>
          <path d="M3 11L3.5 10L4 11L3.5 12L3 11Z" fill="currentColor" opacity="0.7"/>
          <path d="M21 10L21.5 9L22 10L21.5 11L21 10Z" fill="currentColor" opacity="0.7"/>
        </svg>
        <span class="flowerchat-text">AI Floral Assistant</span>
      </button>
      <div class="flowerchat-overlay" aria-hidden="true">
        <div class="flowerchat-menu" role="dialog" aria-modal="true" aria-labelledby="flowerchat-title">
          <div class="flowerchat-header">
            <div class="flowerchat-header-content">
              <span class="flowerchat-h2" id="flowerchat-title">FiftyFlowers DIY FLORAL ASSISTANT</span>
            </div>
            <button class="flowerchat-close" aria-label="Close menu" type="button">×</button>
          </div>
          <div class="flowerchat-tabs-container">
            <div class="flowerchat-tabs-scroll">
              <div class="flowerchat-tabs-list" role="tablist" aria-label="Conversation tabs">
                <!-- Tabs will be dynamically inserted here -->
              </div>
            </div>
            <div class="flowerchat-custom-scrollbar">
              <div class="flowerchat-scrollbar-track">
                <div class="flowerchat-scrollbar-thumb"></div>
              </div>
            </div>
            <div class="flowerchat-context-menu" role="menu" aria-label="Tab options" style="display: none;">
              <button class="flowerchat-context-item" role="menuitem" data-action="rename" type="button">Rename</button>
              <button class="flowerchat-context-item" role="menuitem" data-action="delete" type="button">Delete</button>
            </div>
          </div>
          <div class="flowerchat-content">
            <div class="flowerchat-loading-container">
              <div class="flowerchat-loading-spinner">
                <svg aria-hidden="true" focusable="false" role="presentation" class="flowerchat-spinner-svg" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle class="flowerchat-spinner-path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert into body
    document.body.insertAdjacentHTML("beforeend", chatHTML);

    // Function to position button above Gorgias
    function positionAboveGorgias() {
      const chatButton = document.querySelector(".flowerchat-button");
      if (!chatButton) return;

      // Exact Gorgias button selectors - use the actual button ID first
      const gorgiasSelectors = [
        "#gorgias-chat-messenger-button", // Exact ID from inspector
        '[data-testid="gorgias-chat-messenger-button"]', // Data test ID as backup
        '[id^="gorgias-chat"]', // Any gorgias-chat ID
        "#gorgias-web-messenger-container",
        '[id^="gorgias-web-messenger"]',
        ".gorgias-web-messenger-container",
        'iframe[src*="gorgias"]',
        '[class*="messenger-button-iframe"]', // Class pattern from inspector
        '[class*="gorgias"]',
      ];

      let gorgiasButton = null;
      let gorgiasElement = null;

      // Try to find Gorgias button element
      for (const selector of gorgiasSelectors) {
        gorgiasElement = document.querySelector(selector);
        if (gorgiasElement) {
          // If we found the actual button, use it directly
          if (
            gorgiasElement.tagName === "BUTTON" ||
            gorgiasElement.id === "gorgias-chat-messenger-button"
          ) {
            gorgiasButton = gorgiasElement;
          }
          // If it's an iframe, try to find the button inside or parent container
          else if (gorgiasElement.tagName === "IFRAME") {
            // Try to find button in iframe document (may not work due to CORS)
            try {
              const iframeDoc =
                gorgiasElement.contentDocument ||
                gorgiasElement.contentWindow?.document;
              if (iframeDoc) {
                const buttonInIframe = iframeDoc.querySelector(
                  "#gorgias-chat-messenger-button"
                );
                if (buttonInIframe) {
                  gorgiasButton = gorgiasElement; // Use iframe as reference
                }
              }
            } catch (e) {
              // CORS issue, use iframe container as reference
              gorgiasButton =
                gorgiasElement.closest('[id*="gorgias"], [class*="gorgias"]') ||
                gorgiasElement.parentElement;
            }
          }
          // For containers, try to find the button inside
          else {
            const buttonInside = gorgiasElement.querySelector(
              "#gorgias-chat-messenger-button"
            );
            gorgiasButton = buttonInside || gorgiasElement;
          }
          break;
        }
      }

      if (gorgiasButton) {
        const gorgiasRect = gorgiasButton.getBoundingClientRect();

        // Make sure button is visible and has dimensions
        if (gorgiasRect.width > 0 && gorgiasRect.height > 0) {
          const gorgiasBottom = window.innerHeight - gorgiasRect.bottom;
          const gorgiasRight = window.innerWidth - gorgiasRect.right;
          const spacing = 12; // Space between buttons

          // Position our button above Gorgias, aligned to the right
          chatButton.style.bottom =
            gorgiasBottom + gorgiasRect.height + spacing + "px";
          chatButton.style.right = gorgiasRight + "px";
          chatButton.style.top = "auto";
          chatButton.setAttribute("data-positioned", "true");
        } else {
          // Button found but not visible yet, retry later
          chatButton.setAttribute("data-positioned", "pending");
        }
      } else {
        // Fallback to bottom-right if Gorgias not found
        chatButton.style.bottom = "90px";
        chatButton.style.right = "20px";
        chatButton.style.top = "auto";
        chatButton.setAttribute("data-positioned", "fallback");
      }
    }

    // Wait for Gorgias to load, then position our button
    function waitForGorgias() {
      // Try immediately
      positionAboveGorgias();

      // Also listen for Gorgias widget loaded event
      if (window.GorgiasChat) {
        const initPromise = window.GorgiasChat.init
          ? window.GorgiasChat.init()
          : Promise.resolve();
        initPromise.then(() => {
          setTimeout(positionAboveGorgias, 500); // Give it time to render
        });
      } else {
        window.addEventListener("gorgias-widget-loaded", function () {
          setTimeout(positionAboveGorgias, 500);
        });
      }

      // Fallback: retry after delays
      setTimeout(positionAboveGorgias, 1000);
      setTimeout(positionAboveGorgias, 2000);
      setTimeout(positionAboveGorgias, 3000);

      // Watch for DOM changes in case Gorgias loads dynamically
      const observer = new MutationObserver(() => {
        // Disconnect once successfully positioned (performance optimization)
        if (
          document
            .querySelector(".flowerchat-button")
            ?.getAttribute("data-positioned") === "true"
        ) {
          observer.disconnect();
          return;
        }
        positionAboveGorgias();
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Safety: disconnect observer after 10 seconds regardless (prevents indefinite watching)
      setTimeout(() => observer.disconnect(), 10000);

      // Also reposition on window resize
      window.addEventListener("resize", positionAboveGorgias);
    }

    // Start positioning logic
    waitForGorgias();

    // Get elements
    const chatButton = document.querySelector(".flowerchat-button");
    const overlay = document.querySelector(".flowerchat-overlay");
    const menu = document.querySelector(".flowerchat-menu");
    const contentArea = document.querySelector(".flowerchat-content");

    // Event delegation for filter removal - whole chip is clickable
    if (contentArea) {
      contentArea.addEventListener("click", function (e) {
        // Check if the click was on a filter chip (anywhere on the pill)
        const filterChip = e.target.closest(".flowerchat-filter-chip");
        if (filterChip && !e.target.closest(".flowerchat-clear-all-filters")) {
          e.preventDefault();
          e.stopPropagation();

          // Don't allow filter removal while a message is sending
          const input = contentArea.querySelector(".flowerchat-input");
          if (input && input.disabled) {
            return;
          }

          const filterType = filterChip.dataset.filterType;
          const filterValue = filterChip.dataset.filterValue;
          const isNegative = filterChip.dataset.isNegative === "true";

          // Balloon deflating then pop animation with slow slide
          const chipWidth = filterChip.offsetWidth;
          filterChip.style.width = chipWidth + "px";
          filterChip.style.overflow = "hidden";
          filterChip.style.transition =
            "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease";
          filterChip.style.transform = "scale(0.7)";
          filterChip.style.opacity = "0.7";

          setTimeout(() => {
            // Pop and collapse width for smooth slide of siblings
            filterChip.style.transition =
              "transform 0.15s ease-out, opacity 0.15s ease-out, width 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)";
            filterChip.style.transform = "scale(0)";
            filterChip.style.opacity = "0";
            filterChip.style.width = "0";
            filterChip.style.padding = "0";
            filterChip.style.margin = "0";

            setTimeout(() => {
              filterChip.remove();
              // If no more chips, remove the wrapper (which includes clear-all button)
              const container = contentArea.querySelector(
                ".flowerchat-filters-container"
              );
              if (container && container.children.length === 0) {
                const wrapper = container.closest(
                  ".flowerchat-filters-wrapper"
                );
                if (wrapper) {
                  wrapper.remove();
                } else {
                  container.remove();
                }
              }
            }, 600);
          }, 300);

          if (filterType && typeof removeFilterDirect === "function") {
            removeFilterDirect(filterType, filterValue, isNegative);
          }
        }

        // Check if the click was on the clear all button
        const clearAllButton = e.target.closest(
          ".flowerchat-clear-all-filters"
        );
        if (clearAllButton) {
          e.preventDefault();
          e.stopPropagation();

          // Don't allow while sending
          const input = contentArea.querySelector(".flowerchat-input");
          if (input && input.disabled) {
            return;
          }

          // Animate out ALL filter wrappers immediately (both negative and positive)
          const filtersSection = contentArea.querySelector(
            ".flowerchat-filters-section"
          );
          if (filtersSection) {
            const allWrappers = filtersSection.querySelectorAll(
              ".flowerchat-filters-wrapper"
            );
            allWrappers.forEach((wrapper) => {
              wrapper.style.transition =
                "transform 0.3s ease, opacity 0.3s ease";
              wrapper.style.transform = "scale(0.9)";
              wrapper.style.opacity = "0";

              setTimeout(() => {
                wrapper.remove();
              }, 300);
            });
          }

          // Clear all filters directly (no LLM)
          // jumpToBottom() will be called automatically when typing indicator appears
          if (typeof clearAllFiltersDirect === "function") {
            clearAllFiltersDirect();
          }
        }

        // Check if the click was on a refresh icon button
        const refreshButton = e.target.closest(".flowerchat-refresh-icon");
        if (refreshButton) {
          e.preventDefault();
          e.stopPropagation();

          // Don't allow while sending
          const input = contentArea.querySelector(".flowerchat-input");
          if (input && input.disabled) {
            return;
          }

          // Refresh recommendations with current filters
          if (typeof refreshRecommendations === "function") {
            refreshRecommendations();
          }
        }

        // Carousel arrow clicks
        const carouselArrow = e.target.closest(".flowerchat-carousel-arrow");
        if (carouselArrow) {
          e.preventDefault();
          e.stopPropagation();
          const carousel = carouselArrow.closest(".flowerchat-carousel");
          if (carousel) {
            const dir = parseInt(carouselArrow.dataset.dir);
            navigateCarousel(carousel, dir);
          }
        }

        // Carousel dot clicks
        const carouselDot = e.target.closest(".flowerchat-carousel-dot");
        if (carouselDot) {
          e.preventDefault();
          e.stopPropagation();
          const carousel = carouselDot.closest(".flowerchat-carousel");
          if (carousel) {
            // Find the visible index of this dot (not the original data-index)
            const visibleDots = Array.from(
              carousel.querySelectorAll(".flowerchat-carousel-dot")
            ).filter((d) => d.style.display !== "none");
            const targetIndex = visibleDots.indexOf(carouselDot);
            if (targetIndex >= 0) {
              goToCarouselSlide(carousel, targetIndex);
            }
          }
        }
      });
    }

    // Carousel navigation functions
    function navigateCarousel(carousel, direction) {
      const validImages = Array.from(
        carousel.querySelectorAll(".flowerchat-carousel-image:not(.error)")
      );
      if (validImages.length <= 1) return; // No navigation needed

      const currentActive = carousel.querySelector(
        ".flowerchat-carousel-image.active"
      );
      let currentIdx = validImages.indexOf(currentActive);
      if (currentIdx === -1) currentIdx = 0;

      let nextIdx = currentIdx + direction;

      // Wrap around
      if (nextIdx < 0) nextIdx = validImages.length - 1;
      if (nextIdx >= validImages.length) nextIdx = 0;

      goToCarouselSlide(carousel, nextIdx);
    }

    function goToCarouselSlide(carousel, index) {
      const allImages = carousel.querySelectorAll(".flowerchat-carousel-image");
      const validImages = Array.from(allImages).filter(
        (img) => !img.classList.contains("error")
      );
      const allDots = carousel.querySelectorAll(".flowerchat-carousel-dot");
      const visibleDots = Array.from(allDots).filter(
        (d) => d.style.display !== "none"
      );

      if (validImages.length === 0) return;

      // Clamp index
      if (index >= validImages.length) index = 0;
      if (index < 0) index = validImages.length - 1;

      // Update active states for images
      allImages.forEach((img) => img.classList.remove("active"));
      validImages[index].classList.add("active");

      // Update visible dots to match
      visibleDots.forEach((dot, i) => {
        dot.classList.toggle("active", i === index);
      });

      carousel.dataset.current = index;

      // Hide arrows/dots if only 1 valid image
      if (validImages.length <= 1) {
        carousel
          .querySelectorAll(".flowerchat-carousel-arrow")
          .forEach((a) => (a.style.display = "none"));
        const dotsContainer = carousel.querySelector(
          ".flowerchat-carousel-dots"
        );
        if (dotsContainer) dotsContainer.style.display = "none";
      }
    }

    // Handle carousel image error - auto-advance if active image fails
    window.handleCarouselImageError = function (img) {
      img.classList.add("error");

      const carousel = img.closest(".flowerchat-carousel");
      if (!carousel) return;

      // Hide the corresponding dot for this errored image
      const imgIndex = parseInt(img.dataset.index);
      const dots = carousel.querySelectorAll(".flowerchat-carousel-dot");
      if (dots[imgIndex]) {
        dots[imgIndex].style.display = "none";
      }

      // If this was the active image, advance to next valid one
      if (img.classList.contains("active")) {
        img.classList.remove("active");
        const validImages = carousel.querySelectorAll(
          ".flowerchat-carousel-image:not(.error)"
        );
        if (validImages.length > 0) {
          validImages[0].classList.add("active");
          // Update dots - find first visible dot and activate it
          const visibleDots = Array.from(dots).filter(
            (d) => d.style.display !== "none"
          );
          visibleDots.forEach((d, i) => {
            d.classList.toggle("active", i === 0);
          });
        }
      }

      // Hide controls if only 1 or 0 valid images remain
      const validCount = carousel.querySelectorAll(
        ".flowerchat-carousel-image:not(.error)"
      ).length;
      if (validCount <= 1) {
        carousel
          .querySelectorAll(".flowerchat-carousel-arrow")
          .forEach((a) => (a.style.display = "none"));
        const dotsContainer = carousel.querySelector(
          ".flowerchat-carousel-dots"
        );
        if (dotsContainer) dotsContainer.style.display = "none";
      }
    };

    // Conversation state management
    let currentConversationId = null; // For saved conversations
    let currentTempId = null; // For unsaved tabs
    let conversationsList = []; // Saved conversation objects from Lambda (persist)
    let unsavedTabs = []; // Unsaved tabs (local only, don't persist)
    let activeTabIndex = 0;
    let renamingTabId = null;
    let contextMenuTabId = null;
    let pendingArchives = new Set(); // Track conversations being deleted (to filter on re-init)

    /**
     * Session caching functions
     */
    function cacheConversations(userId, conversations) {
      const cache = {
        conversations: conversations,
        timestamp: Date.now(),
      };
      try {
        sessionStorage.setItem(
          `flowerchat_conversations_${userId}`,
          JSON.stringify(cache)
        );
      } catch (e) {
        // Silently fail if cache cannot be saved
      }
    }

    function getCachedConversations(userId) {
      try {
        const cached = sessionStorage.getItem(
          `flowerchat_conversations_${userId}`
        );
        if (!cached) return null;

        const cache = JSON.parse(cached);
        const fiveMinutes = 5 * 60 * 1000;

        // Check if cache is fresh (< 5 minutes)
        if (Date.now() - cache.timestamp < fiveMinutes) {
          return cache.conversations;
        }

        return null; // Stale cache
      } catch (e) {
        return null;
      }
    }

    function clearConversationCache() {
      const userId = getUserId();
      try {
        sessionStorage.removeItem(`flowerchat_conversations_${userId}`);
      } catch (e) {
        // Silently fail if cache cannot be cleared
      }
    }

    /**
     * Persist pending archives to sessionStorage
     */
    function savePendingArchives(userId) {
      try {
        const archivesArray = Array.from(pendingArchives);
        sessionStorage.setItem(
          `flowerchat_pending_archives_${userId}`,
          JSON.stringify(archivesArray)
        );
      } catch (e) {
        // Silently fail if cannot save
      }
    }

    /**
     * Load pending archives from sessionStorage
     */
    function loadPendingArchives(userId) {
      try {
        const saved = sessionStorage.getItem(
          `flowerchat_pending_archives_${userId}`
        );
        if (saved) {
          const archivesArray = JSON.parse(saved);
          pendingArchives = new Set(archivesArray);
        }
      } catch (e) {
        // Silently fail if cannot load
        pendingArchives = new Set();
      }
    }

    /**
     * Remove a conversation ID from pending archives (when successfully archived)
     */
    function removePendingArchive(conversationId, userId) {
      pendingArchives.delete(conversationId);
      savePendingArchives(userId);
    }

    /**
     * Update conversation in conversationsList
     * @param {string} conversationId - Conversation ID
     * @param {Object} updates - Fields to update
     */
    function updateConversationInList(conversationId, updates) {
      const index = conversationsList.findIndex(
        (conv) =>
          conv.conversation_id === conversationId || conv.id === conversationId
      );
      if (index >= 0) {
        // Update existing conversation
        conversationsList[index] = {
          ...conversationsList[index],
          ...updates,
          conversation_id:
            conversationsList[index].conversation_id ||
            conversationsList[index].id ||
            conversationId,
          id:
            conversationsList[index].id ||
            conversationsList[index].conversation_id ||
            conversationId,
        };
      } else {
        // Add new conversation
        conversationsList.push({
          conversation_id: conversationId,
          id: conversationId,
          title: updates.title || "New Conversation",
          conversation_history: updates.conversation_history || [],
          positive_filters: updates.positive_filters || {},
          negative_filters: updates.negative_filters || {},
          ...updates,
        });
      }
    }

    /**
     * Get user ID - checks for Shopify customer first, then uses guest ID
     * Always checks fresh state (no caching) to handle logout scenarios
     * @returns {string} User ID in format: customer_123 or guest_1234567890_abc123
     */
    function getUserId() {
      // Helper function to safely check if a value is valid (not null, undefined, empty, or 'null' string)
      function isValidId(id) {
        return (
          id !== null &&
          id !== undefined &&
          id !== "" &&
          String(id) !== "null" &&
          String(id).trim() !== ""
        );
      }

      // Check for customer ID in various locations (set by Liquid templates)
      // Priority order: SHG_CUSTOMER > swymCustomerId > Shopify.customer
      // Always check fresh state - never cache values

      // Check SHG_CUSTOMER (Shogun)
      if (
        window.SHG_CUSTOMER &&
        typeof window.SHG_CUSTOMER === "object" &&
        isValidId(window.SHG_CUSTOMER.id)
      ) {
        return `customer_${window.SHG_CUSTOMER.id}`;
      }

      // Check swymCustomerId (SWYM Wishlist)
      if (
        typeof window.swymCustomerId !== "undefined" &&
        isValidId(window.swymCustomerId)
      ) {
        return `customer_${window.swymCustomerId}`;
      }

      // Check if Shopify customer is logged in (ALWAYS check fresh, no caching)
      // This is the most reliable check for current login state
      if (
        window.Shopify &&
        window.Shopify.customer !== null &&
        window.Shopify.customer !== undefined &&
        typeof window.Shopify.customer === "object" &&
        isValidId(window.Shopify.customer.id)
      ) {
        return `customer_${window.Shopify.customer.id}`;
      }

      // Not logged in or all customer checks failed - use guest ID from localStorage
      const guestIdKey = "flowerchat_guest_id";
      let guestId = localStorage.getItem(guestIdKey);

      if (!guestId || guestId.trim() === "") {
        // Generate new guest ID: guest_timestamp_randomstring
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 9);
        guestId = `guest_${timestamp}_${randomString}`;
        localStorage.setItem(guestIdKey, guestId);
      }

      return guestId;
    }

    /**
     * Render filter chips based on Lambda response
     * @param {Object} filters - positive_filters or negative_filters object
     * @param {boolean} isNegative - Whether these are negative filters (default: false)
     * @returns {string} HTML for filter chips
     */
    function renderFilterChips(filters, isNegative = false) {
      if (!filters || Object.keys(filters).length === 0) {
        return ""; // Return empty string instead of "No filters applied"
      }

      const chips = [];

      // POSITIVE FILTERS (only when isNegative = false)
      if (!isNegative) {
        // Color filters
        if (
          filters.colors &&
          Array.isArray(filters.colors) &&
          filters.colors.length > 0
        ) {
          filters.colors.forEach((color) => {
            const colorName = color.charAt(0).toUpperCase() + color.slice(1);
            chips.push(`
              <div class="flowerchat-filter-chip" data-filter-type="colors" data-filter-value="${color}" data-is-negative="false">
                <span>${colorName}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove filter" type="button">×</button>
              </div>
            `);
          });
        }

        // Budget filters
        if (filters.budget) {
          const budget = filters.budget;
          if (budget.max && budget.min) {
            chips.push(`
              <div class="flowerchat-filter-chip" data-filter-type="budget" data-filter-value="range" data-is-negative="false">
                <span>$${budget.min}-$${budget.max}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove filter" type="button">×</button>
              </div>
            `);
          } else if (budget.max) {
            chips.push(`
              <div class="flowerchat-filter-chip" data-filter-type="budget" data-filter-value="max" data-is-negative="false">
                <span>Under $${budget.max}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove filter" type="button">×</button>
              </div>
            `);
          } else if (budget.min) {
            chips.push(`
              <div class="flowerchat-filter-chip" data-filter-type="budget" data-filter-value="min" data-is-negative="false">
                <span>Over $${budget.min}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove filter" type="button">×</button>
              </div>
            `);
          }
        }

        // Occasion filters
        if (
          filters.occasions &&
          Array.isArray(filters.occasions) &&
          filters.occasions.length > 0
        ) {
          filters.occasions.forEach((occasion) => {
            const occasionName =
              occasion.charAt(0).toUpperCase() + occasion.slice(1);
            chips.push(`
              <div class="flowerchat-filter-chip" data-filter-type="occasions" data-filter-value="${occasion}" data-is-negative="false">
                <span>${occasionName}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove filter" type="button">×</button>
              </div>
            `);
          });
        }

        // Flower type filters
        if (
          filters.flower_types &&
          Array.isArray(filters.flower_types) &&
          filters.flower_types.length > 0
        ) {
          filters.flower_types.forEach((flowerType) => {
            const flowerName =
              flowerType.charAt(0).toUpperCase() + flowerType.slice(1);
            chips.push(`
              <div class="flowerchat-filter-chip" data-filter-type="flower_types" data-filter-value="${flowerType}" data-is-negative="false">
                <span>${flowerName}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove flower type filter" type="button">×</button>
              </div>
            `);
          });
        }

        // Season filters
        if (filters.season) {
          const season = filters.season;
          // Format season name - capitalize first letter
          const seasonName = season.charAt(0).toUpperCase() + season.slice(1);
          chips.push(`
            <div class="flowerchat-filter-chip" data-filter-type="season" data-filter-value="${escapeHtml(
              season
            )}" data-is-negative="false">
              <span>${escapeHtml(seasonName)}</span>
              <button class="flowerchat-filter-remove" aria-label="Remove season filter" type="button">×</button>
            </div>
          `);
        }

        // Product type filters (positive - singular string)
        if (filters.product_type) {
          const productType = filters.product_type;
          chips.push(`
            <div class="flowerchat-filter-chip" data-filter-type="product_type" data-filter-value="${escapeHtml(
              productType
            )}" data-is-negative="false">
              <span>${escapeHtml(productType)}</span>
              <button class="flowerchat-filter-remove" aria-label="Remove product type filter" type="button">×</button>
            </div>
          `);
        }

        // Effort level filters (positive - singular string)
        // Show with user-friendly names
        if (filters.effort_level) {
          const effortLevel = filters.effort_level;
          // Map internal values to user-friendly display names
          const effortDisplayNames = {
            low: "Ready-Made",
            medium: "DIY Kit",
            high: "Build Your Own",
          };
          const displayName = effortDisplayNames[effortLevel] || effortLevel;
          chips.push(`
            <div class="flowerchat-filter-chip" data-filter-type="effort_level" data-filter-value="${escapeHtml(
              effortLevel
            )}" data-is-negative="false">
              <span>Effort Level: ${escapeHtml(displayName)}</span>
              <button class="flowerchat-filter-remove" aria-label="Remove effort level filter" type="button">×</button>
            </div>
          `);
        }
      }

      // NEGATIVE FILTERS (only when isNegative = true)
      if (isNegative) {
        // Negative color filters
        if (
          filters.colors &&
          Array.isArray(filters.colors) &&
          filters.colors.length > 0
        ) {
          filters.colors.forEach((color) => {
            const colorName = color.charAt(0).toUpperCase() + color.slice(1);
            chips.push(`
              <div class="flowerchat-filter-chip flowerchat-filter-chip-negative" data-filter-type="colors" data-filter-value="${escapeHtml(
                color
              )}" data-is-negative="true">
                <span>No ${escapeHtml(colorName)}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove negative color filter" type="button">×</button>
              </div>
            `);
          });
        }

        // Negative flower type filters
        if (
          filters.flower_types &&
          Array.isArray(filters.flower_types) &&
          filters.flower_types.length > 0
        ) {
          filters.flower_types.forEach((flowerType) => {
            const flowerName =
              flowerType.charAt(0).toUpperCase() + flowerType.slice(1);
            chips.push(`
              <div class="flowerchat-filter-chip flowerchat-filter-chip-negative" data-filter-type="flower_types" data-filter-value="${escapeHtml(
                flowerType
              )}" data-is-negative="true">
                <span>No ${escapeHtml(flowerName)}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove negative flower filter" type="button">×</button>
              </div>
            `);
          });
        }

        // Negative occasion filters
        if (
          filters.occasions &&
          Array.isArray(filters.occasions) &&
          filters.occasions.length > 0
        ) {
          filters.occasions.forEach((occasion) => {
            chips.push(`
              <div class="flowerchat-filter-chip flowerchat-filter-chip-negative" data-filter-type="occasions" data-filter-value="${escapeHtml(
                occasion
              )}" data-is-negative="true">
                <span>No ${escapeHtml(occasion)}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove negative occasion filter" type="button">×</button>
              </div>
            `);
          });
        }

        // Negative effort levels
        if (
          filters.effort_levels &&
          Array.isArray(filters.effort_levels) &&
          filters.effort_levels.length > 0
        ) {
          filters.effort_levels.forEach((level) => {
            const levelName = level === "high" ? "DIY" : level;
            chips.push(`
              <div class="flowerchat-filter-chip flowerchat-filter-chip-negative" data-filter-type="effort_levels" data-filter-value="${escapeHtml(
                level
              )}" data-is-negative="true">
                <span>Effort Level: No ${escapeHtml(levelName)}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove negative effort filter" type="button">×</button>
              </div>
            `);
          });
        }

        // Negative product types
        if (
          filters.product_types &&
          Array.isArray(filters.product_types) &&
          filters.product_types.length > 0
        ) {
          filters.product_types.forEach((type) => {
            chips.push(`
              <div class="flowerchat-filter-chip flowerchat-filter-chip-negative" data-filter-type="product_types" data-filter-value="${escapeHtml(
                type
              )}" data-is-negative="true">
                <span>No ${escapeHtml(type)}</span>
                <button class="flowerchat-filter-remove" aria-label="Remove negative product type filter" type="button">×</button>
              </div>
            `);
          });
        }

        // Negative season filters
        if (filters.season) {
          const season = filters.season;
          const seasonName = season.charAt(0).toUpperCase() + season.slice(1);
          chips.push(`
            <div class="flowerchat-filter-chip flowerchat-filter-chip-negative" data-filter-type="season" data-filter-value="${escapeHtml(
              season
            )}" data-is-negative="true">
              <span>No ${escapeHtml(seasonName)}</span>
              <button class="flowerchat-filter-remove" aria-label="Remove negative season filter" type="button">×</button>
            </div>
          `);
        }
      }

      if (chips.length === 0) {
        return ""; // Return empty string if no chips were generated
      }

      // Add clear all button above the filters
      const clearAllButton = `
        <button class="flowerchat-clear-all-filters" type="button" aria-label="Clear all active filters">
          Clear Filters
        </button>
      `;

      return `<div class="flowerchat-filters-wrapper">${clearAllButton}<div class="flowerchat-filters-container">${chips.join(
        ""
      )}</div></div>`;
    }

    /**
     * Render variant options for a product
     * @param {Array} variants - Array of variant objects
     * @returns {string} HTML for variant options display
     */
    function renderVariantOptions(variants) {
      if (!variants || variants.length <= 1) {
        return "";
      }

      // Limit to first 5 variants in display, show "X more" if more exist
      const displayVariants = variants.slice(0, 5);
      const remainingCount = variants.length - 5;

      const variantHTML = displayVariants
        .map((v) => {
          // Check if name contains parenthetical note like "(Please allow...)"
          const parenMatch = v.name.match(/^(.+?)(\s*\([^)]+\))$/);
          let nameHTML;
          if (parenMatch) {
            // Split into main name and note
            const mainName = parenMatch[1].trim();
            const note = parenMatch[2].trim();
            nameHTML = `<span class="flowerchat-variant-name">${escapeHtml(
              mainName
            )}<span class="flowerchat-variant-note">${escapeHtml(
              note
            )}</span></span>`;
          } else {
            nameHTML = `<span class="flowerchat-variant-name">${escapeHtml(
              v.name
            )}</span>`;
          }
          return `
        <div class="flowerchat-variant-option">
            ${nameHTML}
          <span class="flowerchat-variant-price">$${v.price.toFixed(2)}</span>
        </div>
        `;
        })
        .join("");

      const moreHTML =
        remainingCount > 0
          ? `<div class="flowerchat-variant-more">+ ${remainingCount} more options</div>`
          : "";

      return `
        <div class="flowerchat-variant-options">
          <div class="flowerchat-variant-header">Available Options:</div>
          ${variantHTML}
          ${moreHTML}
        </div>
      `;
    }

    /**
     * Render products from Lambda response
     * @param {Array} products - Array of product objects
     * @returns {string} HTML for products display
     */
    function renderProducts(products) {
      if (!products || products.length === 0) {
        return "";
      }

      const productsHTML = products
        .map((product) => {
          // Check if product has variants (new structure)
          const hasVariants =
            product.variants &&
            Array.isArray(product.variants) &&
            product.variants.length > 1;

          // Determine price display
          let priceDisplay = "";
          if (hasVariants && product.price_range) {
            // New structure with variants
            priceDisplay = `
            <div class="flowerchat-product-price">
              <span class="flowerchat-from-price">From $${product.price_range.min.toFixed(
                2
              )}</span>
            </div>
            <div class="flowerchat-variant-info">
              ${
                product.variant_count || product.variants.length
              } options available 
            </div>
            <div class="flowerchat-price-range">
                ($${product.price_range.min.toFixed(
                  2
                )} - $${product.price_range.max.toFixed(2)})
            </div>
          `;
          } else {
            // Old structure or single variant - use variant_price or price
            const price = product.variant_price || product.price || 0;
            priceDisplay = `<p class="flowerchat-product-price">$${price.toFixed(
              2
            )}</p>`;
          }

          // Determine product link
          const productLink =
            product.link ||
            (product.handle
              ? `https://fiftyflowers.com/products/${product.handle}`
              : "#");

          // Determine button text
          const buttonText = hasVariants ? "View Options" : "View Product";

          // Product image - show only first image
          let imageUrl = null;
          if (product.image_urls && product.image_urls.length > 0) {
            imageUrl = product.image_urls[0];
          } else if (product.image_url) {
            imageUrl = product.image_url;
          }

          let imageHTML;
          if (imageUrl) {
            imageHTML = `
            <div class="flowerchat-product-image-wrapper">
              <img src="${imageUrl}" alt="${escapeHtml(
              product.product_name
            )}" class="flowerchat-product-image" loading="lazy">
            </div>`;
          } else {
            // No images - show a nice flower placeholder
            imageHTML = `<div class="flowerchat-product-image-wrapper flowerchat-image-placeholder">
               <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                 <circle cx="32" cy="24" r="8" fill="currentColor" opacity="0.2"/>
                 <circle cx="24" cy="28" r="7" fill="currentColor" opacity="0.15"/>
                 <circle cx="40" cy="28" r="7" fill="currentColor" opacity="0.15"/>
                 <circle cx="26" cy="18" r="6" fill="currentColor" opacity="0.15"/>
                 <circle cx="38" cy="18" r="6" fill="currentColor" opacity="0.15"/>
                 <circle cx="32" cy="24" r="5" fill="currentColor" opacity="0.3"/>
                 <path d="M32 32 L32 52 M28 42 Q32 38 36 42" stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.2"/>
               </svg>
               <span class="flowerchat-placeholder-text">No image</span>
             </div>`;
          }

          return `
          <a href="${productLink}" class="flowerchat-product-card" target="_blank">
            <div class="flowerchat-product-header">
              ${
                product.product_type
                  ? `<span class="flowerchat-product-type">${escapeHtml(
                      product.product_type
                    )}</span>`
                  : ""
              }
              <h4 class="flowerchat-product-name">${escapeHtml(
                product.product_name
              )}</h4>
            </div>
            ${imageHTML}
            <div class="flowerchat-product-details">
              <div class="flowerchat-product-price-row">
                ${priceDisplay}
              </div>
            </div>
            <div class="flowerchat-product-actions">
              <span class="flowerchat-product-link">
                ${buttonText}
              </span>
            </div>
          </a>
        `;
        })
        .join("");

      return `
        <div class="flowerchat-products-container">
          <div class="flowerchat-products-grid">
            ${productsHTML}
          </div>
        </div>
      `;
    }

    /**
     * Welcome prompts to show on welcome screen
     */
    const WELCOME_PROMPTS = [
      "Red roses for a wedding",
      "Spring flowers under $100",
      "Pink flowers for Mother's Day",
      "Budget-friendly DIY bouquets",
    ];

    /**
     * Render welcome screen with prompts
     * @returns {string} HTML for welcome screen
     */
    function renderWelcomePrompts() {
      const prompts = WELCOME_PROMPTS.map(
        (prompt) => `
        <button class="flowerchat-prompt-button" data-prompt="${prompt}" type="button">
          ${prompt}
        </button>
      `
      ).join("");

      return `
        <div class="flowerchat-welcome-container">
          <div class="flowerchat-welcome-message">
            <p class="flowerchat-welcome-subtitle">Hi! I'm your FiftyFlowers DIY Floral Assistant. What are you looking for today?</p>
          </div>
          <p class="flowerchat-try-label">Try asking me:</p>
          <div class="flowerchat-prompts-container">
            ${prompts}
          </div>
        </div>
      `;
    }

    /**
     * Render conversation history
     * @param {Array} history - conversation_history array
     * @param {boolean} userExists - whether user exists in system
     * @param {boolean} hasFilters - whether there are active filters
     * @returns {string} HTML for conversation messages
     */
    function renderConversationHistory(history, userExists, hasFilters) {
      const messages = [];

      if (history && Array.isArray(history) && history.length > 0) {
        // Render existing conversation history
        history.forEach((msg) => {
          const role = msg.role || "assistant";
          const message = msg.message || msg.content || "";
          const messageClass =
            role === "user"
              ? "flowerchat-message-user"
              : "flowerchat-message-assistant";

          const productsHTML =
            msg.products && msg.products.length > 0
              ? renderProducts(msg.products)
              : "";

          messages.push(`
            <div class="flowerchat-message ${messageClass}">
              <div class="flowerchat-message-content">
                ${escapeHtml(message)}
              </div>
              ${productsHTML}
            </div>
          `);
        });
      }

      // Add refresh message as last bot message if filters exist
      if (hasFilters) {
        messages.push(`
          <div class="flowerchat-message flowerchat-message-assistant">
            <div class="flowerchat-refresh-message-wrapper">
              <div class="flowerchat-message-content">
                Want new recommendations?
              </div>
              <div class="flowerchat-refresh-message-actions">
                <button class="flowerchat-refresh-icon" type="button" aria-label="Refresh recommendations">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        `);
      }

      return `<div class="flowerchat-messages-container">${messages.join(
        ""
      )}</div>`;
    }

    /**
     * Show toast notification
     * @param {string} message - Message to display
     */
    function showToast(message) {
      // Create toast element if it doesn't exist
      let toast = document.querySelector(".flowerchat-toast");
      if (!toast) {
        toast = document.createElement("div");
        toast.className = "flowerchat-toast";
        document.body.appendChild(toast);
      }

      toast.textContent = message;
      toast.classList.add("flowerchat-toast-show");

      setTimeout(() => {
        toast.classList.remove("flowerchat-toast-show");
      }, 3000);
    }

    /**
     * Archive a conversation
     * @param {string} conversationId - Conversation ID to archive
     * @param {string} userId - User ID
     * @param {boolean} isActive - Whether this is the currently active conversation
     */
    async function archiveConversation(
      conversationId,
      userId,
      isActive = false
    ) {
      if (!conversationId) {
        return;
      }

      const lambdaUrl = window.theme_api_url + "ai-agent";
      const payload = {
        action: "archive_conversation",
        user_id: userId,
        conversation_id: conversationId,
      };

      try {
        const response = await fetch(lambdaUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Find the index before removing
        const currentIndex = conversationsList.findIndex(
          (conv) =>
            conv.conversation_id === conversationId ||
            conv.id === conversationId
        );

        // Remove from conversations list
        conversationsList = conversationsList.filter(
          (conv) =>
            conv.conversation_id !== conversationId &&
            conv.id !== conversationId
        );

        // Clear cache (conversation deleted)
        clearConversationCache();

        // If this was the active conversation, we need to switch to another
        if (isActive) {
          if (conversationsList.length > 0) {
            const nextIndex =
              currentIndex < conversationsList.length
                ? currentIndex
                : conversationsList.length - 1;
            const nextConv = conversationsList[nextIndex];
            switchToConversation(nextConv.conversation_id || nextConv.id);
          } else if (unsavedTabs.length > 0) {
            const nextTab = unsavedTabs[0];
            switchToUnsavedTab(nextTab.temp_id);
          } else {
            createNewUnsavedTab();
          }
        } else {
          renderTabs();

          // Even if not active, if there are no conversations left and no unsaved tabs, create one
          if (
            conversationsList.length === 0 &&
            unsavedTabs.length === 0 &&
            !currentConversationId &&
            !currentTempId
          ) {
            createNewUnsavedTab();
          }
        }

        showToast("Conversation deleted");

        // Remove from pending archives (successfully deleted)
        removePendingArchive(conversationId, userId);
      } catch (error) {
        showToast("Failed to delete conversation");
        // Remove from pending archives even on failure (let it reappear)
        removePendingArchive(conversationId, userId);
      }
    }

    /**
     * Show welcome screen (empty/new conversation)
     * @param {string} userId - User ID
     */
    function showWelcomeScreen(userId) {
      const emptyChatData = {
        success: true,
        user_exists: false,
        conversation_history: [],
        positive_filters: {},
        negative_filters: {},
      };

      renderWelcomeView(emptyChatData, userId);
    }

    /**
     * Render welcome view with prompts
     * @param {Object} data - Lambda response data
     * @param {string} userId - Current user ID
     */
    function renderWelcomeView(data, userId) {
      const welcomeHTML = renderWelcomePrompts();

      contentArea.innerHTML = `
        <div class="flowerchat-messages-wrapper">
          <div class="flowerchat-messages-section flowerchat-welcome-section">
            ${welcomeHTML}
          </div>
          <div class="flowerchat-custom-scrollbar-vertical">
            <div class="flowerchat-scrollbar-track-vertical">
              <div class="flowerchat-scrollbar-thumb-vertical"></div>
            </div>
          </div>
          <button class="flowerchat-scroll-to-bottom" aria-label="Scroll to bottom" type="button" style="display: none;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 13L12 18L17 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="flowerchat-input-section">
          <div class="flowerchat-input-container">
            <input 
              type="text" 
              class="flowerchat-input" 
              placeholder="Type your message..." 
              aria-label="Chat input"
            />
            <button class="flowerchat-send-button" type="button" aria-label="Send message" disabled>
              Send
            </button>
          </div>
        </div>
        <a href="https://fiftyflowers.com/products/personal-consultation-with-our-wedding-floral-expert" target="_blank" rel="noopener noreferrer" class="flowerchat-promo-section">
          <span class="flowerchat-promo-heading flowerchat-h3">Need personalized guidance?</span>
          <p class="flowerchat-promo-text">Schedule a Professional Virtual Consultation, Personalized for your event.</p>
          <span class="flowerchat-promo-link"><span class="flowerchat-promo-link-text">Talk to a Floral Expert</span> →</span>
        </a>
      `;

      // Setup vertical scrollbar after rendering
      setTimeout(setupVerticalScrollbar, 100);

      // Wire up prompt buttons
      wireUpWelcomePrompts(userId);
      // Wire up input and send button
      wireUpMessageSending(userId);

      // Focus input field
      focusInput();
    }

    /**
     * Render conversation tabs (saved + unsaved)
     */
    function renderTabs() {
      const tabsList = document.querySelector(".flowerchat-tabs-list");
      if (!tabsList) {
        return;
      }

      let tabsHTML = "";

      // Render saved conversation tabs (from Lambda)
      conversationsList.forEach((conv, index) => {
        const convId = conv.conversation_id || conv.id;
        const isActive = currentConversationId === convId && !currentTempId;
        const isRenaming = renamingTabId === convId;

        if (isRenaming) {
          tabsHTML += `
            <div class="flowerchat-tab flowerchat-tab-renaming" data-conversation-id="${convId}">
              <input 
                type="text" 
                class="flowerchat-tab-rename-input" 
                value="${conv.title || "New Conversation"}" 
                data-conversation-id="${convId}"
                maxlength="50"
              />
            </div>
          `;
        } else {
          tabsHTML += `
            <div class="flowerchat-tab ${
              isActive ? "flowerchat-tab-active" : ""
            }" 
                 data-conversation-id="${convId}" 
                 data-tab-index="${index}"
                 role="tab"
                 aria-selected="${isActive}">
              <span class="flowerchat-tab-title">${
                conv.title || "New Conversation"
              }</span>
              <button class="flowerchat-tab-close" 
                      data-conversation-id="${convId}" 
                      aria-label="Delete conversation" 
                      type="button"
                      title="Delete">×</button>
              <button class="flowerchat-tab-rename" 
                      data-conversation-id="${convId}" 
                      aria-label="Rename conversation" 
                      type="button"
                      title="Rename">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          `;
        }
      });

      // Render unsaved tabs (local only)
      const totalTabs = conversationsList.length + unsavedTabs.length;
      const isOnlyUnsavedTab = totalTabs === 1 && unsavedTabs.length === 1;

      unsavedTabs.forEach((tab, index) => {
        const tempId = tab.temp_id;
        const isActive = currentTempId === tempId;
        const isRenaming = renamingTabId === tempId;

        if (isRenaming) {
          tabsHTML += `
            <div class="flowerchat-tab flowerchat-tab-renaming flowerchat-tab-unsaved" data-temp-id="${tempId}">
              <input 
                type="text" 
                class="flowerchat-tab-rename-input" 
                value="${tab.title || "New Conversation"}" 
                data-temp-id="${tempId}"
                maxlength="50"
              />
            </div>
          `;
        } else {
          // Hide close button if this is the only tab (and it's an unsaved tab)
          const closeButtonHTML = isOnlyUnsavedTab
            ? ""
            : `
              <button class="flowerchat-tab-close" 
                      data-temp-id="${tempId}" 
                      aria-label="Close tab" 
                      type="button">×</button>
          `;

          // Add class to hide gap when close button is missing
          const noCloseClass = isOnlyUnsavedTab
            ? "flowerchat-tab-no-close"
            : "";

          tabsHTML += `
            <div class="flowerchat-tab flowerchat-tab-unsaved ${
              isActive ? "flowerchat-tab-active" : ""
            } ${noCloseClass}" 
                 data-temp-id="${tempId}" 
                 data-tab-index="${conversationsList.length + index}"
                 role="tab"
                 aria-selected="${isActive}">
              <span class="flowerchat-tab-title">${
                tab.title || "New Conversation"
              }</span>
              ${closeButtonHTML}
            </div>
          `;
        }
      });

      // Check if we should hide the plus button
      // Hide it if the active tab is an unsaved tab with no conversation history
      const activeUnsavedTab = currentTempId
        ? unsavedTabs.find((t) => t.temp_id === currentTempId)
        : null;
      const shouldHidePlusButton =
        activeUnsavedTab &&
        (!activeUnsavedTab.conversation_history ||
          activeUnsavedTab.conversation_history.length === 0);

      // Add [+] button at the end of tabs
      const plusButtonClass = shouldHidePlusButton
        ? "flowerchat-tab-plus-hidden"
        : "";
      tabsHTML += `
        <div class="flowerchat-tab-plus-wrapper">
          <button class="flowerchat-tab-plus ${plusButtonClass}" aria-label="New conversation" type="button" title="New Conversation">
          + New
        </button>
        </div>
      `;

      tabsList.innerHTML = tabsHTML;

      // Wire up tab events
      wireUpTabs();

      // Ensure active tab styling is correct
      if (currentTempId) {
        updateActiveTabStyling(null, currentTempId);
      } else if (currentConversationId) {
        updateActiveTabStyling(currentConversationId, null);
      }

      // Scroll active tab into view
      scrollActiveTabIntoView();

      // Update custom scrollbar after tabs render
      if (window.setupFlowerchatScrollbar) {
        setTimeout(window.setupFlowerchatScrollbar, 100);
      }

      // Check if tabs are overflowing to show/hide plus button shadow
      checkTabsOverflow();
    }

    /**
     * Disable all tabs while a message is loading
     */
    function disableTabs() {
      const tabs = document.querySelectorAll(
        ".flowerchat-tab, .flowerchat-tab-plus"
      );
      tabs.forEach((tab) => {
        tab.classList.add("flowerchat-tab-disabled");
        tab.style.pointerEvents = "none";
        tab.style.opacity = "0.5";
      });
    }

    /**
     * Re-enable all tabs after message response
     */
    function enableTabs() {
      const tabs = document.querySelectorAll(
        ".flowerchat-tab, .flowerchat-tab-plus"
      );
      tabs.forEach((tab) => {
        tab.classList.remove("flowerchat-tab-disabled");
        tab.style.pointerEvents = "";
        tab.style.opacity = "";
      });
    }

    /**
     * Check if tabs container is overflowing and toggle class for plus button shadow
     */
    function checkTabsOverflow() {
      const tabsScroll = document.querySelector(".flowerchat-tabs-scroll");
      const tabsList = document.querySelector(".flowerchat-tabs-list");

      if (tabsScroll && tabsList) {
        const isOverflowing = tabsList.scrollWidth > tabsScroll.clientWidth;
        tabsScroll.classList.toggle("flowerchat-tabs-overflow", isOverflowing);
      }
    }

    /**
     * Wire up tab event handlers
     */
    function wireUpTabs() {
      const tabs = document.querySelectorAll(".flowerchat-tab");

      tabs.forEach((tab) => {
        const conversationId = tab.getAttribute("data-conversation-id");
        const tempId = tab.getAttribute("data-temp-id");
        const isUnsaved = tab.classList.contains("flowerchat-tab-unsaved");
        const isRenaming = tab.classList.contains("flowerchat-tab-renaming");

        if (isRenaming) {
          const input = tab.querySelector(".flowerchat-tab-rename-input");
          if (input) {
            wireUpRenameInput(input, isUnsaved);
          }
        } else {
          // Click to switch tab
          tab.addEventListener("click", function (e) {
            // Don't switch if clicking on action buttons
            if (
              e.target.classList.contains("flowerchat-tab-close") ||
              e.target.classList.contains("flowerchat-tab-rename")
            ) {
              return;
            }

            // Don't allow tab switching while a message is loading
            const input = contentArea.querySelector(".flowerchat-input");
            if (input && input.disabled) {
              return;
            }

            if (isUnsaved && tempId) {
              switchToUnsavedTab(tempId);
            } else if (conversationId) {
              switchToConversation(conversationId);
            }
          });

          // Rename button click (only saved conversations)
          if (!isUnsaved && conversationId) {
            const renameBtn = tab.querySelector(".flowerchat-tab-rename");
            if (renameBtn) {
              renameBtn.addEventListener("click", function (e) {
                e.stopPropagation();
                const convId = this.getAttribute("data-conversation-id");
                const conv = conversationsList.find(
                  (c) => c.conversation_id === convId || c.id === convId
                );
                if (conv) {
                  startRenameMode(convId);
                }
              });
            }

            // Right-click for context menu (only saved conversations)
            tab.addEventListener("contextmenu", function (e) {
              e.preventDefault();
              showContextMenu(conversationId, e);
            });
          }

          // Close button
          const closeBtn = tab.querySelector(".flowerchat-tab-close");
          if (closeBtn) {
            closeBtn.addEventListener("click", function (e) {
              e.stopPropagation();

              // Immediately hide the tab for instant feedback
              tab.style.transition = "opacity 0.15s ease, transform 0.15s ease";
              tab.style.opacity = "0";
              tab.style.transform = "scale(0.9)";
              tab.style.pointerEvents = "none";

              if (isUnsaved) {
                const tempId = this.getAttribute("data-temp-id");
                closeUnsavedTab(tempId);
              } else {
                const convId = this.getAttribute("data-conversation-id");
                const isActive =
                  currentConversationId === convId && !currentTempId;

                // Find current index before removing
                const currentIndex = conversationsList.findIndex(
                  (conv) =>
                    conv.conversation_id === convId || conv.id === convId
                );

                // Immediately remove from local list
                conversationsList = conversationsList.filter(
                  (conv) =>
                    conv.conversation_id !== convId && conv.id !== convId
                );

                // Track as pending archive (in case panel closes before API completes)
                pendingArchives.add(convId);
                savePendingArchives(getUserId()); // Persist immediately

                // Clear cache immediately so deleted conversation doesn't reappear from cache
                clearConversationCache();

                // If active, immediately switch to another tab
                if (isActive) {
                  if (conversationsList.length > 0) {
                    const nextIndex = Math.min(
                      currentIndex,
                      conversationsList.length - 1
                    );
                    const nextConv = conversationsList[nextIndex];
                    switchToConversation(
                      nextConv.conversation_id || nextConv.id
                    );
                  } else if (unsavedTabs.length > 0) {
                    switchToUnsavedTab(unsavedTabs[0].temp_id);
                  } else {
                    createNewUnsavedTab();
                  }
                } else {
                  // Animate tab removal before re-rendering
                  const tabElement = document.querySelector(
                    `[data-conversation-id="${convId}"]`
                  );
                  if (tabElement) {
                    // Animate out
                    tabElement.style.transition =
                      "transform 0.3s ease, opacity 0.3s ease, width 0.3s ease, padding 0.3s ease, margin 0.3s ease";
                    tabElement.style.transform = "scale(0.9)";
                    tabElement.style.opacity = "0";
                    tabElement.style.width = "0";
                    tabElement.style.padding = "0";
                    tabElement.style.margin = "0";
                    tabElement.style.overflow = "hidden";

                    setTimeout(() => {
                      renderTabs();
                    }, 300);
                  } else {
                    renderTabs();
                  }
                }

                // Archive in background (don't wait for it)
                archiveConversation(convId, getUserId(), false);
              }
            });
          }
        }
      });

      // Wire up [+] button (query for element since tabsList is not in scope)
      const tabsListElement = document.querySelector(".flowerchat-tabs-list");
      if (tabsListElement) {
        const plusButton = tabsListElement.querySelector(
          ".flowerchat-tab-plus"
        );

        if (plusButton) {
          // Remove existing event listeners by cloning and replacing
          const newPlusButton = plusButton.cloneNode(true);
          plusButton.parentNode.replaceChild(newPlusButton, plusButton);

          newPlusButton.addEventListener("click", function (e) {
            e.stopPropagation();

            // Don't allow creating new tab while a message is loading
            const input = contentArea.querySelector(".flowerchat-input");
            if (input && input.disabled) {
              return;
            }

            createNewUnsavedTab();
          });
        }
      }
    }

    /**
     * Wire up rename input field
     * @param {HTMLElement} input - Rename input element
     * @param {boolean} isUnsaved - Whether this is an unsaved tab
     */
    function wireUpRenameInput(input, isUnsaved = false) {
      input.focus();
      input.select();

      function saveRename() {
        const conversationIdForRename = input.getAttribute(
          "data-conversation-id"
        );
        const tempIdForRename = input.getAttribute("data-temp-id");
        const newTitle = input.value.trim() || "New Conversation";

        if (isUnsaved && tempIdForRename) {
          // Rename unsaved tab locally
          const tab = unsavedTabs.find((t) => t.temp_id === tempIdForRename);
          if (tab) {
            tab.title = newTitle;
          }
          renamingTabId = null;
          renderTabs();
        } else if (
          conversationIdForRename &&
          conversationIdForRename !== "new"
        ) {
          // Rename saved conversation (calls Lambda)
          if (!conversationIdForRename || !newTitle || !newTitle.trim()) {
            renamingTabId = null;
            renderTabs();
            return;
          }
          saveRenameConversation(conversationIdForRename, newTitle);
        } else {
          renamingTabId = null;
          renderTabs();
        }
      }

      function cancelRename() {
        renamingTabId = null;
        renderTabs();
      }

      input.addEventListener("blur", saveRename);

      input.addEventListener("keydown", function (e) {
        if (e.key === "Enter") {
          e.preventDefault();
          saveRename();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancelRename();
        }
      });
    }

    /**
     * Start rename mode for a tab
     * @param {string} conversationId - Conversation ID to rename
     */
    function startRenameMode(conversationId) {
      if (conversationId === "new" || !conversationId) {
        return;
      }

      renamingTabId = conversationId;
      renderTabs();
    }

    /**
     * Save renamed conversation
     * @param {string} conversationId - Conversation ID
     * @param {string} newTitle - New title
     */
    async function saveRenameConversation(conversationId, newTitle) {
      // Validate inputs
      if (!conversationId || !newTitle || !newTitle.trim()) {
        return;
      }

      const userId = getUserId();
      const lambdaUrl = window.theme_api_url + "ai-agent";
      const payload = {
        action: "rename_conversation",
        user_id: userId,
        conversation_id: conversationId,
        new_title: newTitle.trim(),
      };

      try {
        const response = await fetch(lambdaUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          // Update conversation in list
          const conv = conversationsList.find(
            (c) =>
              c.conversation_id === conversationId || c.id === conversationId
          );

          if (conv) {
            conv.title = newTitle.trim();
          }

          renamingTabId = null;
          renderTabs();
          clearConversationCache();
          showToast("Conversation renamed");
        } else {
          throw new Error("Rename failed - Lambda returned success: false");
        }
      } catch (error) {
        showToast("Failed to rename conversation");
        renamingTabId = null;
        renderTabs();
      }
    }

    /**
     * Show context menu for a tab
     * @param {string} conversationId - Conversation ID
     * @param {Event} e - Mouse event
     */
    function showContextMenu(conversationId, e) {
      if (conversationId === "new") return;

      contextMenuTabId = conversationId;
      const contextMenu = document.querySelector(".flowerchat-context-menu");
      if (!contextMenu) return;

      contextMenu.style.display = "block";
      contextMenu.style.left = e.pageX + "px";
      contextMenu.style.top = e.pageY + "px";

      // Close on outside click
      setTimeout(() => {
        document.addEventListener("click", function closeMenu() {
          contextMenu.style.display = "none";
          contextMenuTabId = null;
          document.removeEventListener("click", closeMenu);
        });
      }, 0);
    }

    /**
     * Create new unsaved tab
     * @returns {string} The temp_id of the created tab
     */
    function createNewUnsavedTab() {
      const tempId = `temp_${Date.now()}`;

      const newTab = {
        temp_id: tempId,
        title: "New Conversation",
        conversation_history: [],
        positive_filters: {},
        negative_filters: {},
      };

      unsavedTabs.push(newTab);
      currentTempId = tempId;
      currentConversationId = null;

      renderTabs();
      showWelcomeScreen(getUserId());

      // Auto-scroll to the new tab after a brief delay
      setTimeout(() => {
        scrollActiveTabIntoView();
      }, 100);

      // Focus input after creating new tab
      focusInput();

      return tempId;
    }

    /**
     * Switch to an unsaved tab
     * @param {string} tempId - Temp ID of the unsaved tab
     */
    function switchToUnsavedTab(tempId) {
      // Check if we're leaving an empty unsaved tab (welcome screen only, no messages)
      if (currentTempId && currentTempId !== tempId) {
        const currentTab = unsavedTabs.find((t) => t.temp_id === currentTempId);
        if (
          currentTab &&
          (!currentTab.conversation_history ||
            currentTab.conversation_history.length === 0)
        ) {
          // Auto-delete empty unsaved tab when switching away
          const currentTabIndex = unsavedTabs.findIndex(
            (t) => t.temp_id === currentTempId
          );
          if (currentTabIndex >= 0) {
            unsavedTabs.splice(currentTabIndex, 1);
          }
        }
      }

      const tabIndex = unsavedTabs.findIndex((t) => t.temp_id === tempId);
      if (tabIndex < 0) {
        return;
      }

      // Update state first
      currentConversationId = null;
      currentTempId = tempId;

      // Show as active immediately (before moving)
      updateActiveTabStyling(null, tempId);

      // Delay before reorganizing tabs
      setTimeout(() => {
        // Move tab to the end (rightmost position, close to + button)
        const tab = unsavedTabs[tabIndex];
        unsavedTabs.splice(tabIndex, 1);
        unsavedTabs.push(tab);

        renderTabs();
        updateActiveTabStyling(null, tempId);
      }, 300); // 300ms delay

      // Show conversation data if it has history, otherwise show welcome
      if (tab.conversation_history && tab.conversation_history.length > 0) {
        const conversationData = {
          conversation_id: null,
          conversation_title: tab.title,
          conversation_history: tab.conversation_history,
          positive_filters: tab.positive_filters || {},
          negative_filters: tab.negative_filters || {},
          user_exists: true,
        };
        renderChatUI(conversationData, getUserId(), { scrollToBottom: true });
      } else {
        showWelcomeScreen(getUserId());
      }

      // Focus input after switching to unsaved tab
      focusInput();
    }

    /**
     * Close an unsaved tab
     * @param {string} tempId - Temp ID of the unsaved tab to close
     */
    function closeUnsavedTab(tempId) {
      const tabIndex = unsavedTabs.findIndex((t) => t.temp_id === tempId);
      if (tabIndex < 0) {
        return;
      }

      const wasActive = currentTempId === tempId;

      // If this was the active tab, switch to another tab first
      if (wasActive) {
        if (unsavedTabs.length > 1) {
          // Switch to another unsaved tab before removing
          const nextTab = unsavedTabs[tabIndex === 0 ? 1 : 0];
          switchToUnsavedTab(nextTab.temp_id);
        } else if (conversationsList.length > 0) {
          const firstConv = conversationsList[0];
          switchToConversation(firstConv.conversation_id || firstConv.id);
        } else {
          createNewUnsavedTab();
        }
      }

      // Animate tab removal before removing from array
      const tabElement = document.querySelector(`[data-temp-id="${tempId}"]`);
      if (tabElement) {
        // Animate out
        tabElement.style.transition =
          "transform 0.3s ease, opacity 0.3s ease, width 0.3s ease, padding 0.3s ease, margin 0.3s ease";
        tabElement.style.transform = "scale(0.9)";
        tabElement.style.opacity = "0";
        tabElement.style.width = "0";
        tabElement.style.padding = "0";
        tabElement.style.margin = "0";
        tabElement.style.overflow = "hidden";

        setTimeout(() => {
          // Remove the tab from array
          unsavedTabs.splice(tabIndex, 1);
          renderTabs();
        }, 300);
      } else {
        // Remove the tab from array immediately if element not found
        unsavedTabs.splice(tabIndex, 1);
        renderTabs();
      }
    }

    /**
     * Switch to a specific saved conversation (uses local data, no Lambda call)
     * @param {string} conversationId - Conversation ID to switch to
     */
    function switchToConversation(conversationId) {
      // Find conversation in local data (don't call Lambda)
      const conversationIndex = conversationsList.findIndex(
        (c) => c.conversation_id === conversationId || c.id === conversationId
      );

      if (conversationIndex < 0) {
        showToast("Conversation not found");
        renderTabs();
        return;
      }

      // Check if we're leaving an empty unsaved tab (welcome screen only, no messages)
      if (currentTempId) {
        const currentTab = unsavedTabs.find((t) => t.temp_id === currentTempId);
        if (
          currentTab &&
          (!currentTab.conversation_history ||
            currentTab.conversation_history.length === 0)
        ) {
          // Auto-delete empty unsaved tab when switching away
          const currentTabIndex = unsavedTabs.findIndex(
            (t) => t.temp_id === currentTempId
          );
          if (currentTabIndex >= 0) {
            unsavedTabs.splice(currentTabIndex, 1);
          }
        }
      }

      // Update state first
      const conversation = conversationsList[conversationIndex];
      currentConversationId = conversation.conversation_id || conversation.id;
      currentTempId = null;

      // Show as active immediately (before moving)
      updateActiveTabStyling(currentConversationId, null);

      // Delay before reorganizing tabs
      setTimeout(() => {
        // FLIP Animation: Capture initial position of the tab before moving
        const convId = conversation.conversation_id || conversation.id;
        const tabElement = document.querySelector(
          `[data-conversation-id="${convId}"]`
        );
        const initialLeft = tabElement ? tabElement.offsetLeft : null;
        const willAnimate =
          initialLeft !== null &&
          conversationIndex !== conversationsList.length - 1;

        // Move conversation to the end (rightmost tab, close to + button)
        conversationsList.splice(conversationIndex, 1);
        conversationsList.push(conversation);

        // Render tabs (skip auto-scroll if we're animating)
        window._skipTabScroll = willAnimate;
        renderTabs();
        window._skipTabScroll = false;

        // FLIP Animation: Animate tab sliding to new position
        if (initialLeft !== null) {
          const newTabElement = document.querySelector(
            `[data-conversation-id="${currentConversationId}"]`
          );
          const tabsScroll = document.querySelector(".flowerchat-tabs-scroll");

          if (newTabElement && tabsScroll) {
            const finalLeft = newTabElement.offsetLeft;
            const deltaX = initialLeft - finalLeft;

            // Only animate if the tab actually moved
            if (deltaX !== 0) {
              // Apply initial transform to appear in old position (no transition yet)
              newTabElement.style.setProperty(
                "transform",
                `translateX(${deltaX}px)`,
                "important"
              );

              // Force reflow to ensure transform is applied
              newTabElement.offsetHeight;

              // Add animation class and animate to final position
              newTabElement.classList.add("flowerchat-tab-animating");
              newTabElement.style.setProperty(
                "transform",
                "translateX(0)",
                "important"
              );

              // Animate scroll to follow the tab
              const targetScroll =
                newTabElement.offsetLeft -
                tabsScroll.offsetWidth / 2 +
                newTabElement.offsetWidth / 2;
              tabsScroll.scrollTo({ left: targetScroll, behavior: "smooth" });

              // Clean up after animation
              setTimeout(() => {
                newTabElement.classList.remove("flowerchat-tab-animating");
                newTabElement.style.removeProperty("transform");
              }, 2000);
            }
          }
        }

        // Ensure active class is correctly applied after render
        updateActiveTabStyling(currentConversationId, null);
      }, 300); // 300ms delay

      // Render conversation instantly (no loading, uses local data)
      const conversationData = {
        conversation_id: conversation.conversation_id || conversation.id,
        conversation_title: conversation.title,
        conversation_history: conversation.conversation_history || [],
        positive_filters: conversation.positive_filters || {},
        negative_filters: conversation.negative_filters || {},
        user_exists: true,
      };

      // ========== DEBUG LOGGING: Switching conversation ==========
      renderChatUI(conversationData, getUserId(), { scrollToBottom: true });

      // Focus input after switching conversation
      focusInput();
    }

    /**
     * Update active tab styling - removes active class from all tabs and adds to specified tab
     * @param {string} conversationId - Conversation ID to make active (null if not a saved conversation)
     * @param {string} tempId - Temp ID to make active (null if not an unsaved tab)
     */
    function updateActiveTabStyling(conversationId, tempId) {
      // Remove active class and aria-selected from all tabs
      const allTabs = document.querySelectorAll(".flowerchat-tab");
      allTabs.forEach((tab) => {
        tab.classList.remove("flowerchat-tab-active");
        tab.setAttribute("aria-selected", "false");
      });

      // Add active class and aria-selected to the specified tab
      if (tempId) {
        const activeTab = document.querySelector(`[data-temp-id="${tempId}"]`);
        if (activeTab) {
          activeTab.classList.add("flowerchat-tab-active");
          activeTab.setAttribute("aria-selected", "true");
        }
      } else if (conversationId) {
        const activeTab = document.querySelector(
          `[data-conversation-id="${conversationId}"]`
        );
        if (activeTab) {
          activeTab.classList.add("flowerchat-tab-active");
          activeTab.setAttribute("aria-selected", "true");
        }
      }
    }

    /**
     * Scroll active tab into view
     */
    function scrollActiveTabIntoView() {
      // Skip if FLIP animation is handling the scroll
      if (window._skipTabScroll) return;

      const tabsScroll = document.querySelector(".flowerchat-tabs-scroll");
      const activeTab = document.querySelector(".flowerchat-tab-active");

      if (tabsScroll && activeTab) {
        const scrollLeft =
          activeTab.offsetLeft -
          tabsScroll.offsetWidth / 2 +
          activeTab.offsetWidth / 2;
        tabsScroll.scrollTo({ left: scrollLeft, behavior: "smooth" });
      }
    }

    /**
     * Wire up welcome prompt buttons
     * @param {string} userId - Current user ID
     */
    function wireUpWelcomePrompts(userId) {
      const promptButtons = contentArea.querySelectorAll(
        ".flowerchat-prompt-button"
      );
      promptButtons.forEach((button) => {
        button.addEventListener("click", function () {
          const promptText = this.getAttribute("data-prompt");
          sendMessage(promptText, userId);
        });
      });
    }

    /**
     * Wire up message sending (input field and send button)
     * @param {string} userId - Current user ID
     */
    function wireUpMessageSending(userId) {
      const input = contentArea.querySelector(".flowerchat-input");
      const sendButton = contentArea.querySelector(".flowerchat-send-button");

      if (!input || !sendButton) return;

      // Enable/disable send button based on input
      function updateSendButton() {
        const hasText = input.value.trim().length > 0;
        sendButton.disabled = !hasText;
      }

      // Update on input
      input.addEventListener("input", updateSendButton);
      updateSendButton(); // Initial state

      // Send on button click
      sendButton.addEventListener("click", function () {
        const message = input.value.trim();
        if (message) {
          sendMessage(message, userId);
        }
      });

      // Send on Enter key (Shift+Enter for new line)
      input.addEventListener("keydown", function (e) {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          const message = input.value.trim();
          if (message) {
            sendMessage(message, userId);
          }
        }
        // Shift+Enter allows new line (default textarea behavior)
        // For text input, Shift+Enter does nothing, Enter sends
      });
    }

    /**
     * Remove a filter directly (no LLM) and resend the query
     * @param {string} filterType - The type of filter (colors, occasions, etc.)
     * @param {string} filterValue - The value to remove
     * @param {boolean} isNegative - Whether this is a negative filter
     */
    async function removeFilterDirect(filterType, filterValue, isNegative) {
      // Get current conversation filters
      let currentPositive = {};
      let currentNegative = {};

      // Get filters from current conversation or unsaved tab
      if (currentConversationId) {
        const conv = conversationsList.find(
          (c) => c.conversation_id === currentConversationId
        );
        if (conv) {
          currentPositive = JSON.parse(
            JSON.stringify(conv.positive_filters || {})
          );
          currentNegative = JSON.parse(
            JSON.stringify(conv.negative_filters || {})
          );
        }
      } else if (currentTempId) {
        const tab = unsavedTabs.find((t) => t.temp_id === currentTempId);
        if (tab) {
          currentPositive = JSON.parse(
            JSON.stringify(tab.positive_filters || {})
          );
          currentNegative = JSON.parse(
            JSON.stringify(tab.negative_filters || {})
          );
        }
      }

      // Remove the filter from local copy
      const targetFilters = isNegative ? currentNegative : currentPositive;

      if (filterType === "budget") {
        delete currentPositive.budget;
      } else if (
        filterType === "season" ||
        filterType === "product_type" ||
        filterType === "effort_level"
      ) {
        delete targetFilters[filterType];
      } else if (Array.isArray(targetFilters[filterType])) {
        targetFilters[filterType] = targetFilters[filterType].filter(
          (v) => v.toLowerCase() !== filterValue.toLowerCase()
        );
        if (targetFilters[filterType].length === 0) {
          delete targetFilters[filterType];
        }
      }

      // Send direct filter update (no LLM)
      await updateFiltersDirect(currentPositive, currentNegative);
    }

    /**
     * Clear all filters directly (no LLM)
     */
    async function clearAllFiltersDirect() {
      // Send direct filter update with empty filters
      await updateFiltersDirect({}, {});
    }

    /**
     * Refresh recommendations with current filters (no LLM)
     */
    async function refreshRecommendations() {
      // Get current conversation filters
      let currentPositive = {};
      let currentNegative = {};

      // Get filters from current conversation or unsaved tab
      if (currentConversationId) {
        const conv = conversationsList.find(
          (c) => c.conversation_id === currentConversationId
        );
        if (conv) {
          currentPositive = JSON.parse(
            JSON.stringify(conv.positive_filters || {})
          );
          currentNegative = JSON.parse(
            JSON.stringify(conv.negative_filters || {})
          );
        }
      } else if (currentTempId) {
        const tab = unsavedTabs.find((t) => t.temp_id === currentTempId);
        if (tab) {
          currentPositive = JSON.parse(
            JSON.stringify(tab.positive_filters || {})
          );
          currentNegative = JSON.parse(
            JSON.stringify(tab.negative_filters || {})
          );
        }
      }

      // Send direct filter update with same filters (refreshes results)
      await updateFiltersDirect(currentPositive, currentNegative);
    }

    /**
     * Update filters directly without LLM - sends to backend and refreshes products
     * @param {Object} positiveFilters - New positive filters
     * @param {Object} negativeFilters - New negative filters
     */
    async function updateFiltersDirect(positiveFilters, negativeFilters) {
      const userId = getUserId();
      const input = contentArea.querySelector(".flowerchat-input");
      const sendButton = contentArea.querySelector(".flowerchat-send-button");

      // Disable input while processing
      if (input) input.disabled = true;
      if (sendButton) sendButton.disabled = true;

      // Show typing indicator (same as sendMessage)
      const messagesSection = contentArea.querySelector(
        ".flowerchat-messages-section"
      );
      let messagesContainer = contentArea.querySelector(
        ".flowerchat-messages-container"
      );

      if (messagesContainer || messagesSection) {
        const typingIndicator = `
          <div class="flowerchat-message flowerchat-message-assistant flowerchat-typing-indicator">
            <div class="flowerchat-message-content">
              <div class="flowerchat-typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        `;
        if (messagesContainer) {
          messagesContainer.insertAdjacentHTML("beforeend", typingIndicator);
        } else {
          messagesSection.insertAdjacentHTML("beforeend", typingIndicator);
        }
        // Immediately jump to bottom to show the loading animation
        setTimeout(() => {
          jumpToBottom();
        }, 50);
      }

      try {
        const lambdaUrl = window.theme_api_url + "ai-agent";

        const response = await fetch(lambdaUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "update_filters",
            user_id: userId,
            conversation_id: currentConversationId,
            positive_filters: positiveFilters,
            negative_filters: negativeFilters,
          }),
        });

        const data = await response.json();

        // Remove typing indicator
        const typingIndicator = contentArea.querySelector(
          ".flowerchat-typing-indicator"
        );
        if (typingIndicator) typingIndicator.remove();

        if (data.success) {
          // Update conversation ID if new
          if (data.conversation_id) {
            currentConversationId = data.conversation_id;
          }

          // Get current conversation for history
          let currentHistory = [];
          let currentTitle = "Conversation";

          if (currentConversationId) {
            const conv = conversationsList.find(
              (c) => c.conversation_id === currentConversationId
            );
            if (conv) {
              currentHistory = conv.conversation_history || [];
              currentTitle = conv.title || "Conversation";
              conv.positive_filters = data.positive_filters || {};
              conv.negative_filters = data.negative_filters || {};
            }
          } else if (currentTempId) {
            const tab = unsavedTabs.find((t) => t.temp_id === currentTempId);
            if (tab) {
              currentHistory = tab.conversation_history || [];
              currentTitle = tab.title || "Conversation";
              tab.positive_filters = data.positive_filters || {};
              tab.negative_filters = data.negative_filters || {};
            }
          }

          // Use history from response if available, otherwise keep current
          const historyToUse =
            data.conversation_history && data.conversation_history.length > 0
              ? data.conversation_history
              : currentHistory;

          // Re-render the chat UI with updated data
          const updatedConversationData = {
            conversation_id: currentConversationId,
            conversation_title: currentTitle,
            conversation_history: historyToUse,
            positive_filters: data.positive_filters || {},
            negative_filters: data.negative_filters || {},
            user_exists: true,
          };

          renderChatUI(updatedConversationData, userId);

          // Scroll to top of product list after response comes in (not to bottom)
          setTimeout(() => {
            scrollToLastMessage();
          }, 200);
        } else {
          console.error("[updateFiltersDirect] Failed:", data.error);
        }
      } catch (error) {
        console.error("[updateFiltersDirect] Error:", error);
        // Remove typing indicator on error
        const typingIndicator = contentArea.querySelector(
          ".flowerchat-typing-indicator"
        );
        if (typingIndicator) typingIndicator.remove();
      } finally {
        // Re-enable input
        if (input) input.disabled = false;
        if (sendButton) sendButton.disabled = !input?.value?.trim();

        // Re-enable tabs
        enableTabs();
      }
    }

    /**
     * Send message to Lambda and update UI
     * @param {string} messageText - Message to send
     * @param {string} userId - User ID
     */
    async function sendMessage(messageText, userId) {
      // Validate
      if (!messageText || !messageText.trim()) {
        return;
      }

      // Determine if this is an unsaved tab
      const isUnsavedTab =
        currentConversationId === null && currentTempId !== null;
      const isNewConversation = currentConversationId === null;

      const input = contentArea.querySelector(".flowerchat-input");
      const sendButton = contentArea.querySelector(".flowerchat-send-button");

      // Disable input and send button
      if (input) input.disabled = true;
      if (sendButton) sendButton.disabled = true;

      // Disable tabs while message is loading
      disableTabs();

      // Add user message to UI immediately
      // First, ensure messages container exists (hide welcome screen if showing)
      const welcomeContainer = contentArea.querySelector(
        ".flowerchat-welcome-container"
      );
      if (welcomeContainer) {
        welcomeContainer.style.display = "none";
      }

      // Get or create messages container
      let messagesContainer = contentArea.querySelector(
        ".flowerchat-messages-container"
      );
      if (!messagesContainer) {
        const messagesSection = contentArea.querySelector(
          ".flowerchat-messages-section"
        );
        if (messagesSection) {
          // Remove welcome-section class if present to ensure proper message alignment
          messagesSection.classList.remove("flowerchat-welcome-section");
          messagesSection.innerHTML =
            '<div class="flowerchat-messages-container"></div>';
          messagesContainer = contentArea.querySelector(
            ".flowerchat-messages-container"
          );
        }
      }

      if (messagesContainer) {
        const userMessageHTML = `
          <div class="flowerchat-message flowerchat-message-user">
            <div class="flowerchat-message-content">
              ${escapeHtml(messageText)}
            </div>
          </div>
        `;
        messagesContainer.insertAdjacentHTML("beforeend", userMessageHTML);
        // Scroll to bottom when user sends message
        setTimeout(() => {
          scrollToBottom();
        }, 50);
      }

      // Clear input
      if (input) {
        input.value = "";
      }

      // Show typing indicator with bouncing dots
      if (messagesContainer) {
        const typingIndicator = `
          <div class="flowerchat-message flowerchat-message-assistant flowerchat-typing-indicator">
            <div class="flowerchat-message-content">
              <div class="flowerchat-typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        `;
        messagesContainer.insertAdjacentHTML("beforeend", typingIndicator);
        // Don't scroll here - wait for bot response to scroll to top of new message
      }

      const lambdaUrl = window.theme_api_url + "ai-agent/process";
      const payload = {
        action: "process",
        user_id: userId,
        conversation_id: currentConversationId || null, // null for unsaved tabs (will become saved after first message)
        message: messageText.trim(),
      };

      try {
        const response = await fetch(lambdaUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Remove typing indicator
        const typingIndicator = contentArea.querySelector(
          ".flowerchat-typing-indicator"
        );
        if (typingIndicator) {
          typingIndicator.remove();
        }

        if (!data.success) {
          showToast("Failed to send message");
          if (input) input.disabled = false;
          if (sendButton) sendButton.disabled = false;

          // Re-enable tabs
          enableTabs();
          return;
        }

        // Handle conversion from unsaved tab to saved conversation
        const isUnsavedTabCheck = currentTempId && !currentConversationId;

        if (isUnsavedTabCheck && data.conversation_id) {
          // This was an unsaved tab - convert it to a saved conversation
          const unsavedTabIndex = unsavedTabs.findIndex(
            (t) => t.temp_id === currentTempId
          );
          if (unsavedTabIndex >= 0) {
            unsavedTabs.splice(unsavedTabIndex, 1);

            // Add to saved conversations list
            conversationsList.push({
              conversation_id: data.conversation_id,
              id: data.conversation_id,
              title: data.conversation_title || "New Conversation",
              conversation_history: data.conversation_history || [],
              positive_filters: data.positive_filters || {},
              negative_filters: data.negative_filters || {},
            });

            currentConversationId = data.conversation_id;
            currentTempId = null;
            renderTabs();
          }
        } else if (data.conversation_id && !currentConversationId) {
          // New conversation (not from unsaved tab)
          conversationsList.push({
            conversation_id: data.conversation_id,
            id: data.conversation_id,
            title: data.conversation_title || "New Conversation",
            conversation_history: data.conversation_history || [],
            positive_filters: data.positive_filters || {},
            negative_filters: data.negative_filters || {},
          });
          currentConversationId = data.conversation_id;
          currentTempId = null;
          renderTabs();
        } else if (currentConversationId) {
          // Existing saved conversation - update it
          const convIndex = conversationsList.findIndex(
            (c) =>
              c.conversation_id === currentConversationId ||
              c.id === currentConversationId
          );

          if (convIndex !== -1) {
            const conv = conversationsList[convIndex];

            conv.conversation_history =
              data.conversation_history || conv.conversation_history || [];
            // Always use response filters (backend is source of truth)
            // Use nullish coalescing to handle undefined/null, but use empty object if explicitly provided
            conv.positive_filters =
              data.positive_filters !== undefined
                ? data.positive_filters || {}
                : conv.positive_filters || {};
            conv.negative_filters =
              data.negative_filters !== undefined
                ? data.negative_filters || {}
                : conv.negative_filters || {};

            if (data.conversation_title) {
              conv.title = data.conversation_title;
            }
          }
        } else if (currentTempId) {
          // Unsaved tab - update it locally (but still no conversation_id)
          const unsavedTab = unsavedTabs.find(
            (t) => t.temp_id === currentTempId
          );
          if (unsavedTab) {
            unsavedTab.conversation_history =
              data.conversation_history ||
              unsavedTab.conversation_history ||
              [];
            // Always use response filters (backend is source of truth)
            unsavedTab.positive_filters =
              data.positive_filters !== undefined
                ? data.positive_filters || {}
                : unsavedTab.positive_filters || {};
            unsavedTab.negative_filters =
              data.negative_filters !== undefined
                ? data.negative_filters || {}
                : unsavedTab.negative_filters || {};
            if (data.conversation_title) {
              unsavedTab.title = data.conversation_title;
            }
          }
        }

        // Build full conversation history
        const fullHistory = data.conversation_history || [];

        // If history not in response, reconstruct from current messages + new response
        if (fullHistory.length === 0) {
          const existingMessages = contentArea.querySelectorAll(
            ".flowerchat-message"
          );
          const existingHistory = [];
          existingMessages.forEach((msgEl) => {
            const isUser = msgEl.classList.contains("flowerchat-message-user");
            const content =
              msgEl.querySelector(".flowerchat-message-content")?.textContent ||
              "";
            if (content && !content.includes("typing")) {
              existingHistory.push({
                role: isUser ? "user" : "assistant",
                message: content,
              });
            }
          });
          // Add new assistant message
          if (data.response_message) {
            existingHistory.push({
              role: "assistant",
              message: data.response_message,
              products: data.products || [],
            });
          }
          fullHistory.push(...existingHistory);
        } else {
          // Lambda returned history - ensure latest assistant message has products if provided
          if (
            fullHistory.length > 0 &&
            data.products &&
            data.products.length > 0
          ) {
            const lastMessage = fullHistory[fullHistory.length - 1];
            if (lastMessage && lastMessage.role === "assistant") {
              lastMessage.products = data.products;
            }
          }
        }

        // Determine conversation ID and title for rendering
        const conversationIdForUI =
          currentConversationId || data.conversation_id;
        const conversationTitleForUI =
          data.conversation_title ||
          (currentTempId
            ? unsavedTabs.find((t) => t.temp_id === currentTempId)?.title
            : undefined) ||
          (currentConversationId
            ? conversationsList.find(
                (c) =>
                  c.conversation_id === currentConversationId ||
                  c.id === currentConversationId
              )?.title
            : undefined) ||
          "New Conversation";

        // Re-render UI with updated conversation data
        const updatedConversationData = {
          conversation_id: conversationIdForUI,
          conversation_title: conversationTitleForUI,
          conversation_history: fullHistory,
          positive_filters: data.positive_filters || {},
          negative_filters: data.negative_filters || {},
          user_exists: true,
        };

        // Clear sessionStorage cache (conversation updated)
        clearConversationCache();

        renderChatUI(updatedConversationData, userId);

        // Re-enable input and send button
        if (input) input.disabled = false;
        if (sendButton) sendButton.disabled = true; // Disabled until user types

        // Re-enable tabs
        enableTabs();

        // Focus input so user can continue typing
        focusInput();
      } catch (error) {
        // Remove typing indicator
        const typingIndicator = contentArea.querySelector(
          ".flowerchat-typing-indicator"
        );
        if (typingIndicator) {
          typingIndicator.remove();
        }

        // Show error message
        if (messagesContainer) {
          const errorMessageHTML = `
            <div class="flowerchat-message flowerchat-message-assistant flowerchat-error">
              <div class="flowerchat-message-content">
                Sorry, I encountered an error. Please try again.
              </div>
            </div>
          `;
          messagesContainer.insertAdjacentHTML("beforeend", errorMessageHTML);
          scrollToBottom();
        }

        // Re-enable input and send button
        if (input) {
          input.disabled = false;
          input.value = messageText; // Restore message text
        }
        if (sendButton) sendButton.disabled = false;

        // Re-enable tabs
        enableTabs();

        showToast("Failed to send message");
      }
    }

    /**
     * Focus the message input field
     */
    function focusInput() {
      const input = contentArea?.querySelector(".flowerchat-input");
      if (input) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          input.focus();
        }, 100);
      }
    }

    /**
     * Scroll messages container to bottom with smooth animation
     */
    function scrollToBottom() {
      const messagesSection = contentArea.querySelector(
        ".flowerchat-messages-section"
      );
      if (messagesSection) {
        // Use smooth scrolling behavior
        messagesSection.scrollTo({
          top: messagesSection.scrollHeight,
          behavior: "smooth",
        });
        // Trigger scrollbar update
        const scrollEvent = new Event("scroll");
        messagesSection.dispatchEvent(scrollEvent);
      }
    }

    /**
     * Immediately jump to bottom (no smooth scroll) - used when filters are cleared
     */
    function jumpToBottom() {
      const messagesSection = contentArea.querySelector(
        ".flowerchat-messages-section"
      );
      if (messagesSection) {
        // Immediate jump to bottom
        messagesSection.scrollTop = messagesSection.scrollHeight;
        // Trigger scrollbar update
        const scrollEvent = new Event("scroll");
        messagesSection.dispatchEvent(scrollEvent);
      }
    }

    /**
     * Adjust messages section padding based on filter rows
     * Base: 70px for 1-2 rows, add ~35px for each additional row (3rd, 4th)
     */
    function adjustMessagesPaddingForFilters() {
      const messagesSection = contentArea.querySelector(
        ".flowerchat-messages-section"
      );
      const filtersSection = contentArea.querySelector(
        ".flowerchat-filters-section"
      );

      if (!messagesSection || !filtersSection) return;

      const filtersHeight = filtersSection.offsetHeight;

      // Base padding for 1-2 rows (filters height ~0-60px)
      // Each additional row adds ~30-35px to the height
      let padding = 70; // base padding

      if (filtersHeight > 90) {
        // 4+ rows
        padding = 140;
      } else if (filtersHeight > 60) {
        // 3 rows
        padding = 105;
      }

      messagesSection.style.paddingBottom = padding + "px";
    }

    /**
     * Scroll so the last assistant message is at the top of the visible area
     * This allows users to read from top to bottom (typical chatbot behavior for new messages)
     */
    function scrollToLastMessage() {
      const messagesSection = contentArea.querySelector(
        ".flowerchat-messages-section"
      );
      if (!messagesSection) return;

      // Get all message elements
      const messages = messagesSection.querySelectorAll(".flowerchat-message");
      if (messages.length === 0) return;

      // Find the last assistant message that contains products (skip refresh button messages)
      let lastAssistantMessage = null;
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (
          msg.classList.contains("flowerchat-message-assistant") &&
          !msg.classList.contains("flowerchat-typing-indicator")
        ) {
          // Skip messages with refresh button - we want the actual product response
          if (
            msg.querySelector(".flowerchat-refresh-icon") ||
            msg.querySelector(".flowerchat-refresh-message-actions")
          ) {
            continue;
          }
          // Prefer messages with products, but accept any assistant message
          if (msg.querySelector(".flowerchat-products-container")) {
            lastAssistantMessage = msg;
            break;
          }
          // If no products message found yet, keep this as fallback
          if (!lastAssistantMessage) {
            lastAssistantMessage = msg;
          }
        }
      }

      // If no assistant message found, use the last message
      if (!lastAssistantMessage) {
        lastAssistantMessage = messages[messages.length - 1];
      }

      // Calculate the scroll position needed to show the message at the top
      // Use getBoundingClientRect to get accurate positions
      const sectionRect = messagesSection.getBoundingClientRect();
      const messageRect = lastAssistantMessage.getBoundingClientRect();

      // Calculate current scroll position
      const currentScrollTop = messagesSection.scrollTop;

      // Calculate the message's position relative to the messages section's content
      // The difference in getBoundingClientRect gives us the visual offset
      // We need to add the current scroll to get the absolute position in the scrollable content
      const messageTopRelativeToSection =
        messageRect.top - sectionRect.top + currentScrollTop;

      // Scroll with small offset (20px from top) so message starts at top with minimal whitespace
      const scrollOffset = 20;
      const targetScroll = messageTopRelativeToSection - scrollOffset;

      // Use smooth scrolling to position message at top
      messagesSection.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: "smooth",
      });

      // Trigger scrollbar update
      const scrollEvent = new Event("scroll");
      messagesSection.dispatchEvent(scrollEvent);
    }

    /**
     * Render the full chat UI based on Lambda response
     * @param {Object} data - Lambda response data
     * @param {string} userId - Current user ID
     * @param {Object} options - Optional settings
     * @param {boolean} options.scrollToBottom - If true, scroll to bottom (for tab switching). Default: false (scroll to last message for new messages)
     */
    function renderChatUI(data, userId, options = {}) {
      const positiveFilters = data.positive_filters || {};
      const negativeFilters = data.negative_filters || {};
      const history = data.conversation_history || [];
      const userExists =
        data.user_exists !== undefined ? data.user_exists : false;

      // Update current conversation ID if provided
      if (data.conversation_id) {
        currentConversationId = data.conversation_id;
        currentTempId = null;
      }

      // If no history and user doesn't exist and no conversation ID, show welcome screen
      if (
        (!history || history.length === 0) &&
        !userExists &&
        !currentConversationId
      ) {
        showWelcomeScreen(userId);
        renderTabs();
        return;
      }

      renderTabs();

      const positiveChips = renderFilterChips(positiveFilters, false);
      const negativeChips = renderFilterChips(negativeFilters, true);

      // Combine filters - negative container first, then positive container
      let filtersHTML = "";
      if (positiveChips && negativeChips) {
        // Negative filters container first, then positive filters container
        filtersHTML = `${negativeChips}${positiveChips}`;
      } else if (negativeChips) {
        filtersHTML = negativeChips;
      } else if (positiveChips) {
        filtersHTML = positiveChips;
      } else {
        filtersHTML =
          '<div class="flowerchat-filters-empty">No filters applied</div>';
      }

      const hasFilters = !!(positiveChips || negativeChips);
      const messagesHTML = renderConversationHistory(
        history,
        userExists,
        hasFilters
      );

      contentArea.innerHTML = `
        <div class="flowerchat-messages-wrapper">
          <div class="flowerchat-messages-section">
            ${messagesHTML}
          </div>
          <div class="flowerchat-custom-scrollbar-vertical">
            <div class="flowerchat-scrollbar-track-vertical">
              <div class="flowerchat-scrollbar-thumb-vertical"></div>
            </div>
          </div>
          <button class="flowerchat-scroll-to-bottom" aria-label="Scroll to bottom" type="button" style="display: none;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 13L12 18L17 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="flowerchat-filters-section">
            ${filtersHTML}
          </div>
        </div>
        <div class="flowerchat-input-section">
          <div class="flowerchat-input-container">
            <input 
              type="text" 
              class="flowerchat-input" 
              placeholder="Type your message..." 
              aria-label="Chat input"
            />
            <button class="flowerchat-send-button" type="button" aria-label="Send message" disabled>
              Send
            </button>
          </div>
        </div>
        <a href="https://fiftyflowers.com/products/personal-consultation-with-our-wedding-floral-expert" target="_blank" rel="noopener noreferrer" class="flowerchat-promo-section">
          <span class="flowerchat-promo-heading flowerchat-h3">Need personalized guidance?</span>
          <p class="flowerchat-promo-text">Schedule a Professional Virtual Consultation, Personalized for your event.</p>
          <span class="flowerchat-promo-link"><span class="flowerchat-promo-link-text">Talk to a Floral Expert</span> →</span>
        </a>
      `;

      // Setup vertical scrollbar after rendering
      setTimeout(setupVerticalScrollbar, 100);

      // Adjust padding based on filter rows
      setTimeout(adjustMessagesPaddingForFilters, 50);

      // Wire up input and send button
      wireUpMessageSending(userId);

      // Scroll behavior: scroll to bottom for tab switching, scroll to last message for new messages
      if (options.scrollToBottom) {
        // For tab switching: start from top and smoothly scroll to bottom
        setTimeout(() => {
          scrollToBottom();
        }, 150);
      } else {
        // For new messages: prevent jump by setting scroll position immediately, then smooth scroll
        // First, immediately set scroll to prevent jump to top
        requestAnimationFrame(() => {
          const messagesSection = contentArea.querySelector(
            ".flowerchat-messages-section"
          );
          if (messagesSection) {
            // Find the target message and set scroll position immediately (no smooth)
            const messages = messagesSection.querySelectorAll(
              ".flowerchat-message"
            );
            if (messages.length > 0) {
              let targetMessage = null;
              for (let i = messages.length - 1; i >= 0; i--) {
                const msg = messages[i];
                if (
                  msg.classList.contains("flowerchat-message-assistant") &&
                  !msg.classList.contains("flowerchat-typing-indicator")
                ) {
                  if (
                    msg.querySelector(".flowerchat-refresh-icon") ||
                    msg.querySelector(".flowerchat-refresh-message-actions")
                  ) {
                    continue;
                  }
                  if (msg.querySelector(".flowerchat-products-container")) {
                    targetMessage = msg;
                    break;
                  }
                  if (!targetMessage) {
                    targetMessage = msg;
                  }
                }
              }

              if (targetMessage) {
                const sectionRect = messagesSection.getBoundingClientRect();
                const messageRect = targetMessage.getBoundingClientRect();
                const currentScrollTop = messagesSection.scrollTop;
                const messageTopRelativeToSection =
                  messageRect.top - sectionRect.top + currentScrollTop;
                const scrollOffset = 20;
                const targetScroll = Math.max(
                  0,
                  messageTopRelativeToSection - scrollOffset
                );

                // Set immediately to prevent jump
                messagesSection.scrollTop = targetScroll;
              }
            }
          }

          // Then smooth scroll to final position after a brief delay
          setTimeout(() => {
            scrollToLastMessage();
          }, 50);
        });
      }

      // Focus input field
      focusInput();
    }

    /**
     * Call Lambda ai-agent function
     * @param {string} userId - User ID to send to Lambda
     */
    async function callLambda(userId) {
      // Check if theme_api_url is available (set by custom-variables.liquid)
      if (!window.theme_api_url) {
        if (contentArea) {
          contentArea.innerHTML =
            "<p><strong>Error:</strong> API URL not configured. Please check theme settings.</p>";
        }
        return;
      }

      const lambdaUrl = window.theme_api_url + "ai-agent";

      const payload = {
        action: "initialize",
        user_id: userId,
      };

      try {
        const response = await fetch(lambdaUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Normalize conversations list - ensure all have conversation_id, history, filters
        if (data.conversations && Array.isArray(data.conversations)) {
          conversationsList = data.conversations
            // Filter out any conversations that are pending deletion
            .filter((conv) => {
              const convId = conv.conversation_id || conv.id;
              return !pendingArchives.has(convId);
            })
            .map((conv) => ({
              conversation_id: conv.conversation_id || conv.id,
              id: conv.id || conv.conversation_id,
              title: conv.title || "New Conversation",
              conversation_history: conv.conversation_history || [],
              positive_filters: conv.positive_filters || {},
              negative_filters: conv.negative_filters || {},
              ...conv,
            }));
        } else if (data.conversation_id) {
          // Single conversation response - add to list if not already there
          const exists = conversationsList.find(
            (conv) =>
              conv.conversation_id === data.conversation_id ||
              conv.id === data.conversation_id
          );
          if (!exists) {
            conversationsList.push({
              conversation_id: data.conversation_id,
              id: data.conversation_id,
              title: data.conversation_title || "New Conversation",
              conversation_history: data.conversation_history || [],
              positive_filters: data.positive_filters || {},
              negative_filters: data.negative_filters || {},
            });
          } else {
            // Update existing conversation with full data
            updateConversationInList(data.conversation_id, {
              conversation_history: data.conversation_history || [],
              positive_filters: data.positive_filters || {},
              negative_filters: data.negative_filters || {},
              title: data.conversation_title || exists.title,
            });
          }
        }

        // Cache conversations for faster reopening
        cacheConversations(userId, conversationsList);

        // Render tabs and UI
        renderTabs();

        // Only switch to a conversation if we're not already on a new unsaved tab
        const isOnUnsavedTab =
          currentTempId !== null && currentConversationId === null;

        if (!isOnUnsavedTab) {
          // Determine which view to show based on response
          if (
            data.conversations &&
            Array.isArray(data.conversations) &&
            data.conversations.length > 0
          ) {
            // Has conversations - load most recent or first

            // Check for active_conversation object first (preferred)
            if (
              data.active_conversation &&
              data.active_conversation.conversation_id
            ) {
              currentConversationId = data.active_conversation.conversation_id;
              currentTempId = null;
              // Update the conversation in list with active_conversation data
              updateConversationInList(
                data.active_conversation.conversation_id,
                {
                  conversation_history:
                    data.active_conversation.conversation_history || [],
                  positive_filters:
                    data.active_conversation.positive_filters || {},
                  negative_filters:
                    data.active_conversation.negative_filters || {},
                  title:
                    data.active_conversation.conversation_title ||
                    data.active_conversation.title,
                }
              );
              renderTabs();
              if (contentArea) {
                renderChatUI(data.active_conversation, userId);
              }
            } else if (data.active_conversation_id) {
              // Fallback to active_conversation_id string
              currentConversationId = data.active_conversation_id;
              currentTempId = null;
              switchToConversation(data.active_conversation_id);
            } else if (conversationsList.length > 0) {
              // Load most recent conversation (first in list)
              const mostRecent = conversationsList[0];
              currentConversationId =
                mostRecent.conversation_id || mostRecent.id;
              currentTempId = null;
              switchToConversation(currentConversationId);
            } else {
              // No conversations - create a new unsaved tab
              createNewUnsavedTab();
            }
          } else if (data.conversation_id && !isOnUnsavedTab) {
            // Single conversation response (from initialize) - only switch if not on unsaved tab
            currentConversationId = data.conversation_id;
            currentTempId = null;
            renderTabs();
            if (contentArea) {
              renderChatUI(data, userId);
            }
          } else if (!isOnUnsavedTab) {
            // No conversation data - create a new unsaved tab (only if not already on one)
            createNewUnsavedTab();
          }
        }
      } catch (error) {
        // Detect CORS errors specifically
        const isCorsError =
          error.message.includes("Failed to fetch") ||
          error.message.includes("CORS") ||
          error.name === "TypeError";

        // Update UI with error (but don't break the UI)
        if (contentArea) {
          if (isCorsError) {
            contentArea.innerHTML = `
              <p><strong>CORS Configuration Error</strong></p>
              <p>User ID: <code>${userId}</code></p>
              <p><strong>Issue:</strong> The Lambda API Gateway needs to be configured to allow CORS requests from this domain.</p>
              <p><strong>Backend fix needed:</strong></p>
              <ul style="text-align: left; margin: 10px 0;">
                <li>Enable CORS on the API Gateway <code>/ai-agent</code> endpoint</li>
                <li>Add <code>Access-Control-Allow-Origin</code> header</li>
                <li>Allow <code>POST</code> method and <code>Content-Type</code> header</li>
              </ul>
              <p><small>Lambda URL: <code>${lambdaUrl}</code></small></p>
              <p><small>Check console for more details.</small></p>
            `;
          } else {
            contentArea.innerHTML = `
              <p><strong>Connection Error</strong></p>
              <p>User ID: <code>${userId}</code></p>
              <p>Error: <code>${error.message}</code></p>
              <p><small>Lambda URL: <code>${lambdaUrl}</code></small></p>
              <p><small>Check console for more details.</small></p>
            `;
          }
        }
      }
    }

    // Open menu function
    async function openMenu() {
      // Lazy-load full CSS on first open
      await loadFullCss();

      overlay.classList.add("active");
      document.body.style.overflow = "hidden"; // Prevent body scroll when menu is open
      // Hide the button when menu opens
      if (chatButton) {
        chatButton.style.display = "none";
      }

      // Get user ID
      const userId = getUserId();

      // Load pending archives first (so we can filter them out)
      loadPendingArchives(userId);

      // Always create a new unsaved tab and make it active when menu opens
      createNewUnsavedTab();

      // Check for cached conversations and load them in background
      const cachedConversations = getCachedConversations(userId);
      if (cachedConversations && cachedConversations.length > 0) {
        // Filter out any conversations pending deletion
        conversationsList = cachedConversations.filter((conv) => {
          const convId = conv.conversation_id || conv.id;
          return !pendingArchives.has(convId);
        });
        renderTabs();

        // Still call Lambda in background to refresh cache, but don't wait
        callLambda(userId).catch(() => {
          // Silently fail background refresh
        });
      } else {
        // No cache - call Lambda to load conversations in background
        callLambda(userId).catch(() => {
          // Silently fail initialization
        });
      }
    }

    // Close menu function
    function closeMenu() {
      overlay.classList.remove("active");
      document.body.style.overflow = ""; // Restore body scroll
      // Show the button when menu closes
      if (chatButton) {
        chatButton.style.display = "flex";
      }

      // Clear unsaved tabs when menu closes (they don't persist)
      unsavedTabs = [];
      currentTempId = null;
      currentConversationId = null;
    }

    // Event listeners
    if (chatButton) {
      chatButton.addEventListener("click", function (e) {
        e.stopPropagation();
        openMenu();
      });
    }

    // Close button in header
    const closeButton = document.querySelector(".flowerchat-close");
    if (closeButton) {
      closeButton.addEventListener("click", function (e) {
        e.stopPropagation();
        closeMenu();
      });
    }

    // Track text selection drag from input field
    let isDraggingFromInput = false;
    let mouseDownInInput = false;

    // Track when mouse down happens in input
    document.addEventListener("mousedown", function (e) {
      const input = contentArea?.querySelector(".flowerchat-input");
      if (input && (e.target === input || input.contains(e.target))) {
        mouseDownInInput = true;
        isDraggingFromInput = false;
      } else {
        mouseDownInInput = false;
      }
    });

    // Track if user is dragging (mousemove after mousedown in input)
    document.addEventListener("mousemove", function (e) {
      if (mouseDownInInput) {
        isDraggingFromInput = true;
      }
    });

    // Reset flags on mouseup
    document.addEventListener("mouseup", function () {
      if (mouseDownInInput && isDraggingFromInput) {
        // User was dragging from input - don't close menu
        // Reset flags after a short delay to allow click handler to check
        setTimeout(() => {
          mouseDownInInput = false;
          isDraggingFromInput = false;
        }, 0);
      } else {
        mouseDownInInput = false;
        isDraggingFromInput = false;
      }
    });

    // Close when clicking overlay (but not the menu itself)
    if (overlay) {
      overlay.addEventListener("click", function (e) {
        if (e.target === overlay) {
          // Don't close if user was dragging from input field
          if (!isDraggingFromInput) {
            closeMenu();
          }
        }
      });
    }

    // Prevent menu clicks from closing
    if (menu) {
      menu.addEventListener("click", function (e) {
        e.stopPropagation();
      });
    }

    // Close on Escape key
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && overlay.classList.contains("active")) {
        closeMenu();
      }
    });

    // [+] Button is now in tabs list and wired up in wireUpTabs()

    // Context menu event handlers
    const contextMenu = document.querySelector(".flowerchat-context-menu");
    if (contextMenu) {
      contextMenu.addEventListener("click", function (e) {
        const item = e.target.closest(".flowerchat-context-item");
        if (!item || !contextMenuTabId) return;

        const action = item.getAttribute("data-action");
        const userId = getUserId();

        contextMenu.style.display = "none";

        if (action === "rename") {
          startRenameMode(contextMenuTabId);
        } else if (action === "delete") {
          archiveConversation(
            contextMenuTabId,
            userId,
            contextMenuTabId === currentConversationId
          );
        }

        contextMenuTabId = null;
      });
    }

    // Custom scrollbar functionality
    let scrollbarSetup = false;
    let updateScrollbarFn = null;

    window.setupFlowerchatScrollbar = function setupCustomScrollbar() {
      if (scrollbarSetup) {
        // If already set up, just update the scrollbar
        if (updateScrollbarFn) updateScrollbarFn();
        return;
      }

      const tabsScroll = document.querySelector(".flowerchat-tabs-scroll");
      const scrollbarTrack = document.querySelector(
        ".flowerchat-scrollbar-track"
      );
      const scrollbarThumb = document.querySelector(
        ".flowerchat-scrollbar-thumb"
      );

      if (!tabsScroll || !scrollbarTrack || !scrollbarThumb) {
        setTimeout(setupCustomScrollbar, 100);
        return;
      }

      scrollbarSetup = true;

      updateScrollbarFn = function updateScrollbar() {
        const scrollWidth = tabsScroll.scrollWidth;
        const clientWidth = tabsScroll.clientWidth;
        const scrollLeft = tabsScroll.scrollLeft;
        const maxScroll = scrollWidth - clientWidth;

        // Check if scrolling is needed
        if (scrollWidth <= clientWidth) {
          scrollbarTrack.closest(".flowerchat-custom-scrollbar").style.display =
            "none";
          return;
        }

        scrollbarTrack.closest(".flowerchat-custom-scrollbar").style.display =
          "block";

        // Calculate thumb width and position
        const trackWidth = scrollbarTrack.offsetWidth;
        const thumbWidth = Math.max(
          (clientWidth / scrollWidth) * trackWidth,
          40
        );
        const thumbLeft =
          maxScroll > 0
            ? (scrollLeft / maxScroll) * (trackWidth - thumbWidth)
            : 0;

        scrollbarThumb.style.width = thumbWidth + "px";
        scrollbarThumb.style.left = thumbLeft + "px";
      };

      // Update on scroll
      tabsScroll.addEventListener("scroll", updateScrollbarFn);

      // Update on resize
      window.addEventListener("resize", updateScrollbarFn);

      // Initial update
      updateScrollbarFn();
      setTimeout(updateScrollbarFn, 500); // Also check after tabs render

      // Dragging functionality
      let isDragging = false;
      let startX = 0;
      let startScrollLeft = 0;

      scrollbarThumb.addEventListener("mousedown", function (e) {
        isDragging = true;
        startX = e.clientX;
        startScrollLeft = tabsScroll.scrollLeft;
        e.preventDefault();
        e.stopPropagation();
      });

      document.addEventListener("mousemove", function (e) {
        if (!isDragging) return;

        const deltaX = e.clientX - startX;
        const trackWidth = scrollbarTrack.offsetWidth;
        const thumbWidth = scrollbarThumb.offsetWidth;
        const scrollWidth = tabsScroll.scrollWidth;
        const clientWidth = tabsScroll.clientWidth;
        const maxScroll = scrollWidth - clientWidth;

        if (maxScroll <= 0) return;

        const scrollDelta = (deltaX / (trackWidth - thumbWidth)) * maxScroll;
        tabsScroll.scrollLeft = Math.max(
          0,
          Math.min(maxScroll, startScrollLeft + scrollDelta)
        );
      });

      document.addEventListener("mouseup", function () {
        isDragging = false;
      });

      // Click on track to jump
      scrollbarTrack.addEventListener("click", function (e) {
        if (e.target === scrollbarThumb || isDragging) return;

        const trackRect = scrollbarTrack.getBoundingClientRect();
        const clickX = e.clientX - trackRect.left;
        const trackWidth = trackRect.width;
        const thumbWidth = scrollbarThumb.offsetWidth;
        const scrollWidth = tabsScroll.scrollWidth;
        const clientWidth = tabsScroll.clientWidth;
        const maxScroll = scrollWidth - clientWidth;

        if (maxScroll <= 0) return;

        const thumbLeft = clickX - thumbWidth / 2;
        const normalizedThumbLeft = Math.max(
          0,
          Math.min(trackWidth - thumbWidth, thumbLeft)
        );
        const scrollPosition =
          (normalizedThumbLeft / (trackWidth - thumbWidth)) * maxScroll;

        tabsScroll.scrollLeft = scrollPosition;
      });
    };

    setupCustomScrollbar();

    // Setup vertical scrollbar for messages section
    function setupVerticalScrollbar() {
      const messagesSection = document.querySelector(
        ".flowerchat-messages-section"
      );
      const scrollbarTrack = document.querySelector(
        ".flowerchat-scrollbar-track-vertical"
      );
      const scrollbarThumb = document.querySelector(
        ".flowerchat-scrollbar-thumb-vertical"
      );
      const scrollbarContainer = document.querySelector(
        ".flowerchat-custom-scrollbar-vertical"
      );

      if (
        !messagesSection ||
        !scrollbarTrack ||
        !scrollbarThumb ||
        !scrollbarContainer
      ) {
        // Retry after a short delay if elements aren't ready
        setTimeout(setupVerticalScrollbar, 100);
        return;
      }

      const scrollToBottomBtn = document.querySelector(
        ".flowerchat-scroll-to-bottom"
      );

      function updateVerticalScrollbar() {
        const scrollHeight = messagesSection.scrollHeight;
        const clientHeight = messagesSection.clientHeight;
        const scrollTop = messagesSection.scrollTop;
        const maxScroll = scrollHeight - clientHeight;
        const threshold = 100; // Show button if more than 100px from bottom

        // Check if scrolling is needed
        if (scrollHeight <= clientHeight) {
          scrollbarContainer.style.display = "none";
          if (scrollToBottomBtn) scrollToBottomBtn.style.display = "none";
          return;
        }

        scrollbarContainer.style.display = "block";

        // Calculate thumb height and position (vertical)
        const trackHeight = scrollbarTrack.offsetHeight;
        const thumbHeight = Math.max(
          (clientHeight / scrollHeight) * trackHeight,
          40
        );
        const thumbTop =
          maxScroll > 0
            ? (scrollTop / maxScroll) * (trackHeight - thumbHeight)
            : 0;

        scrollbarThumb.style.height = thumbHeight + "px";
        scrollbarThumb.style.top = thumbTop + "px";

        // Show/hide scroll-to-bottom button
        if (scrollToBottomBtn) {
          if (maxScroll - scrollTop > threshold) {
            scrollToBottomBtn.style.display = "flex";
          } else {
            scrollToBottomBtn.style.display = "none";
          }
        }
      }

      // Update on scroll
      messagesSection.addEventListener("scroll", updateVerticalScrollbar);

      // Update on resize
      window.addEventListener("resize", updateVerticalScrollbar);

      // Update when content changes (use MutationObserver or check periodically)
      const resizeObserver = new ResizeObserver(updateVerticalScrollbar);
      resizeObserver.observe(messagesSection);

      // Initial update
      updateVerticalScrollbar();
      setTimeout(updateVerticalScrollbar, 500); // Also check after content renders

      // Dragging functionality
      let isDragging = false;
      let startY = 0;
      let startScrollTop = 0;

      scrollbarThumb.addEventListener("mousedown", function (e) {
        isDragging = true;
        startY = e.clientY;
        startScrollTop = messagesSection.scrollTop;
        e.preventDefault();
        e.stopPropagation();
      });

      document.addEventListener("mousemove", function (e) {
        if (!isDragging) return;

        const deltaY = e.clientY - startY;
        const trackHeight = scrollbarTrack.offsetHeight;
        const thumbHeight = scrollbarThumb.offsetHeight;
        const scrollHeight = messagesSection.scrollHeight;
        const clientHeight = messagesSection.clientHeight;
        const maxScroll = scrollHeight - clientHeight;

        if (maxScroll <= 0) return;

        const scrollDelta = (deltaY / (trackHeight - thumbHeight)) * maxScroll;
        messagesSection.scrollTop = Math.max(
          0,
          Math.min(maxScroll, startScrollTop + scrollDelta)
        );
      });

      document.addEventListener("mouseup", function () {
        isDragging = false;
      });

      // Click on track to jump
      scrollbarTrack.addEventListener("click", function (e) {
        if (e.target === scrollbarThumb || isDragging) return;

        const trackRect = scrollbarTrack.getBoundingClientRect();
        const clickY = e.clientY - trackRect.top;
        const trackHeight = trackRect.height;
        const thumbHeight = scrollbarThumb.offsetHeight;
        const scrollHeight = messagesSection.scrollHeight;
        const clientHeight = messagesSection.clientHeight;
        const maxScroll = scrollHeight - clientHeight;

        if (maxScroll <= 0) return;

        const thumbTop = clickY - thumbHeight / 2;
        const normalizedThumbTop = Math.max(
          0,
          Math.min(trackHeight - thumbHeight, thumbTop)
        );
        const scrollPosition =
          (normalizedThumbTop / (trackHeight - thumbHeight)) * maxScroll;

        messagesSection.scrollTop = scrollPosition;
      });

      // Scroll-to-bottom button click handler
      if (scrollToBottomBtn) {
        scrollToBottomBtn.addEventListener("click", function () {
          scrollToBottom();
        });
      }
    }

    // Initial setup
    setupVerticalScrollbar();
  }
})();
